<template>
  <div
    class="shadow-box"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousemove="mousePos = { x: $event.clientX, y: $event.clientY }"
  >
    <div class="search-container">
      <div style="width: 100%">
        <div class="bank-ledger-title">Ledger (Quickbooks Transactions)</div>
        <div class="title-container">
          <div class="account-selection">
            <label style="color: #fff" for="account-select"
              >Select Account:</label
            >
            <select
              id="account-select"
              v-model="selectedAccount"
              @change="handleAccountChange"
            >
              <option
                v-for="account in accountOptions"
                :key="account.accountNumber"
                :value="account.accountNumber"
              >
                {{ account.accountNumber }} : {{ account.accountName }}
              </option>
            </select>
            <button
              :class="
                this.noDoubleEntry
                  ? 'select-credits-button-faded'
                  : 'select-credits-button'
              "
              @click="confirmAccount"
            >
              <i class="fas fa-filter"></i>
            </button>
          </div>
        </div>
        <div
          style="
            width: 100%;
            display: flex;
            gap: 3px;
            height: 36px;
            margin-bottom: -5px;
            justify-content: left;
            align-items: center;
          "
        >
          <button
            title="new entry"
            class="select-credits-button"
            @click="addNewEntry"
          >
            <i class="fas fa-plus"></i>
          </button>
          <input
            v-model="rawSearchQuery"
            ref="searchQuery"
            class="search-input"
            type="text"
            placeholder="Search..."
          />
          <button
            title="clear search"
            @click="clearSearch"
            class="clear-button"
          >
            <i class="fa fa-times-circle"></i>
          </button>
          <button
            title="shortcut keys"
            @click="showShortcuts"
            class="clear-button"
          >
            <i class="fa fa-question-circle"></i>
          </button>
          <div
            class="row-count"
            style="display: inline-block; min-width: 160px"
          >
            showing : {{ visibleTransactionCount }} of {{ totalRows }}
          </div>
          <span class="from-to-label">
            <div class="month-shortcuts">
              <button
                v-for="yearOffset in [2, 1, 0]"
                :key="'year-' + yearOffset"
                class="year-button"
                :class="{
                  'year-selected':
                    selectedYear === new Date().getFullYear() - yearOffset,
                }"
                @click="setYearRange(new Date().getFullYear() - yearOffset)"
                :title="`Select year ${new Date().getFullYear() - yearOffset}`"
              >
                {{ String(new Date().getFullYear() - yearOffset).slice(-2) }}
              </button>
              <span class="year-month-gap"></span>
              <button
                v-for="month in monthShortcuts"
                :key="month.short"
                class="month-button"
                @click="setMonthRange(month.name)"
                :title="`Set date range to ${month.name} ${selectedYear}`"
              >
                {{ month.short }}
              </button>
            </div>
            <input
              @change="debouncedfetchTransactions"
              class="date-picker"
              type="text"
              placeholder="MM/DD/YY"
              :value="formatDateShort(ledgerFromDate)"
              @input="updateFromDate"
              @blur="validateFromDate"
            />
            to
            <input
              @change="debouncedfetchTransactions"
              class="date-picker"
              type="text"
              placeholder="MM/DD/YY"
              :value="formatDateShort(ledgerToDate)"
              @input="updateToDate"
              @blur="validateToDate"
            />
            <button
              title="reset date range to year-to-date"
              class="clear-button"
              @click="resetDateRange('ytd')"
            >
              YT
            </button>
            <button
              title="reset date range to recent period"
              class="clear-button"
              @click="resetDateRange"
            >
              <i style="font-size: 0.8em" class="fa fa-calendar"></i>
            </button>
            <button
              title="refresh"
              class="clear-button"
              @click="fetchTransactions"
            >
              <i style="font-size: 0.8em" class="fa fa-sync"></i>
            </button>
          </span>
          <button
            style="margin-left: 5px"
            :title="'show splits in register\nor press s'"
            class="select-credits-button"
            @click="showSplits"
          >
            <i class="fas fa-align-justify"></i>
          </button>
          <button
            :title="'show deleted in register\n'"
            :class="
              showDeletedRows
                ? 'select-credits-button-faded'
                : 'select-credits-button'
            "
            @click="showDeleted"
          >
            <i class="fas fa-code-commit"></i>
          </button>
          <button
            title="print friendly"
            :class="
              isPrinterFriendly
                ? 'select-credits-button-faded'
                : 'select-credits-button'
            "
            @click="printerFriendly"
          >
            PF
          </button>
          <button
            title="print"
            class="select-credits-button"
            @click="print('printableLedgerTable')"
          >
            <i class="fas fa-print"></i>
          </button>
          <button
            title="export filtered transactions to Excel"
            class="select-credits-button"
            @click="exportTransactions"
          >
            <i class="fas fa-file-excel"></i>
          </button>
          <button
            title="unmatched"
            :class="
              findUnmatchedMode
                ? 'select-credits-button-faded'
                : 'select-credits-button'
            "
            @click="findUnmatched"
          >
            <i class="fas fa-chain-broken"></i>
          </button>
          <button
            title="tagged"
            :class="
              filterTaggedMode
                ? 'select-credits-button-faded'
                : 'select-credits-button'
            "
            @click="filterTagged"
          >
            <i class="fas fa-tag"></i>
          </button>
          <button
            title="filter utilities transactions"
            :class="
              filterUtilitiesMode
                ? 'select-credits-button-faded'
                : 'select-credits-button'
            "
            @click="filterUtilities"
          >
            <i class="fas fa-bolt"></i>
          </button>
          <button
            title="verify data errors"
            class="select-credits-button"
            @click="setVerifySearch"
          >
            <i class="fas fa-check"></i>
          </button>
          <button
            :title="'print check\nor press p'"
            class="select-credits-button"
            @click="printCheck('preview')"
          >
            <i class="fas fa-money-check"></i>
          </button>
          <button
            title="share current search"
            class="select-credits-button"
            @click="shareCurrentSearch"
          >
            <i class="fas fa-share-nodes"></i>
          </button>
          <div
            style="display: flex; justify-content: right"
            class="selected-row-totals"
          >
            <div v-if="selectedRowTotals.count > 0">
              {{ selectedRowTotals.count }} rows
            </div>
            <div v-if="selectedRowTotals.totalCredits > 0">
              , credits =&nbsp;
            </div>
            {{ formatNumber(selectedRowTotals.totalCredits) }}
            <div v-if="selectedRowTotals.totalDebits < 0">, debits =&nbsp;</div>
            {{ formatNumber(selectedRowTotals.totalDebits) }}
          </div>
          <button
            style="margin-left: auto"
            title="clear selections in table"
            @click="clearSelection"
            class="clear-button"
          >
            <i class="fa fa-minus-circle"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="table-container" ref="tableContainer">
      <table id="printableLedgerTable" class="table">
        <thead>
          <tr>
            <th
              v-for="header in headers"
              :key="header"
              :class="{
                'right-align': header === 'Amount',
                'center-align': header === 'Type' || header === 'Num',
                'wide-column': header === 'Name',
                'sortable-header': header === 'Num',
                'num-column': header === 'Num',
              }"
              @click="header === 'Num' ? toggleNumSort() : null"
              :style="header === 'Num' ? 'cursor: pointer;' : ''"
            >
              {{ header }}
              <span
                v-if="header === 'Num' && numSortOrder !== 'none'"
                class="sort-indicator"
              >
                {{ numSortOrder === 'desc' ? '↓' : '↑' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="table-scrollable-body">
          <tr
            v-for="(transaction, index) in filteredTransactions"
            @click="handleRowClick(transaction, $event)"
            @dblclick="
              selectedRow = transaction;
              handleRowDoubleClick(transaction, $event);
            "
            :id="'row-' + transaction.Id"
            :key="transaction.Id"
            :class="{
              even: index % 2 === 0,
              odd: index % 2 !== 0,
              'selected-row': isSelected(transaction.Id),
              'splits-member': transaction.LinkedSplitId != null,
              'boundary-row': isBoundaryRow(index),
              'transaction-deleted': transaction.Deleted == 1,
            }"
          >
            <td
              v-for="(value, key) in stripId(transaction)"
              :key="key"
              :class="{
                voided: key === 'Voided',
                'right-align': key === 'Amount',
                'wide-column':
                  key === 'Name' ||
                  key === 'Memo' ||
                  key === 'Account' ||
                  key === 'Class' ||
                  key === 'Split',
                'center-align': key === 'CheckNum' || key === 'Type',
                'num-column': key === 'CheckNum',
                'icon-column':
                  key === 'Matched' ||
                  key === 'LinkedTransactionId' ||
                  key === 'Tagged',
                matched: key === 'Matched',
              }"
              :title="
                key === 'Matched' && value === 1
                  ? transaction.LinkedTransactionId
                  : key === 'Split'
                    ? transaction.LinkedSplitId
                    : key === 'Date'
                      ? transaction.Id
                      : value
              "
            >
              <!-- Conditional rendering for the 'Tagged' column -->
              <div v-if="key === 'Tagged'">
                <span v-if="value === 1" class="checkmark">&#x2713;</span>
                <span v-else></span>
              </div>
              <!-- Conditional rendering for the 'Matched' column -->
              <div v-else-if="key === 'Matched'">
                <span v-if="value === 1" class="checkmark">&#x2713;</span>
                <span v-else></span>
              </div>
              <div v-else-if="key === 'Voided'">
                <span v-if="value === 1" class="checkmark">&#x2713;</span>
                <span v-else></span>
              </div>
              <div v-else-if="key === 'Deleted'">
                <span v-if="value === 1" class="checkmark">&#x2713;</span>
                <span v-else></span>
              </div>
              <div v-else-if="key === 'linkedDocId'">
                <span style="color: #a080a8" v-if="value !== null" class=""
                  ><i class="fas fa-book"></i
                ></span>
                <span v-else></span>
              </div>
              <!-- Existing fields rendering -->
              <div v-else-if="key === 'Amount'">
                <span v-html="highlightSearch(formatNumber(value))"></span>
              </div>
              <div
                v-else-if="
                  key === 'Name' ||
                  key === 'Account' ||
                  key === 'Split' ||
                  key === 'Class' ||
                  key === 'Memo'
                "
                :class="noWrapText ? '' : 'text-ellipsis'"
                :title="
                  key === 'Name' ? 'Ctrl+Click to search by this name' : value
                "
                @click.ctrl.exact.left.stop.prevent="
                  key === 'Name' && searchByName(value)
                "
                v-html="highlightSearch(value)"
              ></div>
              <div v-else v-html="highlightSearch(value)"></div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="nothing-found">
        <span class="action-link" @click="clearSearch">Clear</span> or
        <span @click="focusSearch" class="action-link">change</span> the search
        filter, or
        <span class="action-link" @click="expandSearch">expand</span>
        the search.
      </div>
    </div>
    <div class="transactions-footer">
      <div v-if="footerMessage != ''" class="footer-message">
        {{ footerMessage }}
      </div>
    </div>

    <div v-if="showSplitDetails" style="margin-top: 10px">
      <div class="title-splits-details">
        Splits Details
        <span class="splits-total"
          >Split Total: {{ formatNumber(splitsTotal) }}</span
        >
      </div>
      <div class="table-container-splits" ref="tableContainerSplits">
        <table class="table">
          <thead>
            <tr>
              <!-- Headers identical to the main table -->
              <th
                v-for="header in headers"
                :key="header"
                :class="{
                  'right-align': header === 'Amount',
                  'wide-column': header === 'Name',
                }"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="table-scrollable-body">
            <!-- Cells identical to the main table -->
            <tr
              v-for="(detail, index) in splitDetails"
              @dblclick="handleRowDoubleClick(detail, $event)"
              :key="detail.Id"
              :class="{
                even: index % 2 === 0,
                odd: index % 2 !== 0,
                'selected-row': isSelected(detail.Id),
              }"
              @click="handleRowClick(detail, $event)"
            >
              <td
                v-for="(value, key) in stripId(detail)"
                :key="key"
                :class="{
                  'right-align': key === 'Amount',
                  'wide-column':
                    key === 'Name' ||
                    key === 'Memo' ||
                    key === 'Account' ||
                    key === 'Class' ||
                    key === 'Split',
                  'center-align': key === 'CheckNum' || key === 'Type',
                  'num-column': key === 'CheckNum',
                  'icon-column':
                    key === 'Matched' ||
                    key === 'LinkedTransactionId' ||
                    key === 'Tagged',
                }"
                :title="
                  key === 'Matched' && value === 1
                    ? transaction.LinkedTransactionId
                    : ''
                "
              >
                <!-- Conditional rendering for the 'Tagged' column -->
                <div v-if="key === 'Tagged'">
                  <span v-if="value === 1" class="checkmark">&#x2713;</span>
                  <span v-else></span>
                </div>
                <!-- Conditional rendering for the 'Matched' column -->
                <div v-else-if="key === 'Matched'">
                  <span v-if="value === 1" class="matched checkmark"
                    >&#x2713;</span
                  >
                  <span v-else class="matched"></span>
                </div>
                <div v-else-if="key === 'Amount'">
                  <span v-html="highlightSearch(formatNumber(value))"></span>
                </div>
                <div
                  v-else-if="
                    key === 'Name' ||
                    key === 'Account' ||
                    key == 'Split' ||
                    key === 'Class' ||
                    key === 'Memo'
                  "
                  class="text-ellipsis"
                  :title="value ? value : ''"
                  v-html="highlightSearch(value)"
                ></div>
                <div v-else v-html="highlightSearch(value)"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="close-split-details">
        <button @click="closeSplitDetails">Close</button>
      </div>
    </div>

    <LedgerEntry
      v-if="showEditDialog"
      :id="selectedRow?.Id"
      :data="newLedgerRow"
      :account="selectedAccount + ' : ' + selectedAccountName"
      :isNew="isNew"
    />
  </div>

  <!-- check or PDF viewer -->
  <div
    v-if="pdfUrl && showPDF"
    :class="[
      'draggable-popup',
      viewerTitle === 'Check Viewer' ? 'check-viewer' : 'ledger-viewer',
    ]"
    ref="pdfViewer"
    @dblclick="handleDoubleClick"
  >
    <div class="check-viewer-header">
      <div class="check-viewer-title">
        {{ viewerTitle }}
      </div>
      <button class="check-viewer-close-button" @click="navigateCheck(-1)">
        <i class="fas fa-arrow-left"></i>
      </button>
      <button class="check-viewer-close-button" @click="navigateCheck(1)">
        <i class="fas fa-arrow-right"></i>
      </button>
      <button class="check-viewer-close-button" @click="printCheck('preview')">
        <i class="fas fa-refresh"></i>
      </button>
      <button class="check-viewer-close-button" @click="printCheck()">
        <i class="fas fa-print"></i>
      </button>
      <button class="check-viewer-close-button" @click="showPDF = false">
        X
      </button>
    </div>
    <div style="position: relative; width: 100%; height: 100%">
      <iframe
        id="pdf-frame"
        :src="pdfUrl"
        frameborder="0"
        style="width: 100%; height: 100%; z-index: 1"
      ></iframe>
      <div
        class="pdf-overlay"
        @dblclick="handleDoubleClick"
        :style="{
          position: 'absolute',
          top: '221px',
          left: '55px',
          width: '208px',
          height: '64px',
          zIndex: 1000,
          background: 'transparent',
          pointerEvents: 'auto',
          border: '1px dotted blue',
        }"
      ></div>
    </div>
  </div>

  <!-- Share URL popup -->
  <div v-if="showSharePopup" class="confirm-popup">
    <div class="confirm-message">
      <h3>Share Current Ledger Search</h3>
      <p>Copy this URL to share the current ledger search with others:</p>
      <input
        type="text"
        :value="shareUrl"
        readonly
        style="width: 100%; margin: 10px 0; padding: 5px"
        @click="$event.target.select()"
      />
    </div>
    <div class="confirm-buttons">
      <button @click="copyToClipboard">Copy URL</button>
      <button @click="showSharePopup = false">Close</button>
    </div>
  </div>

  <!-- Delete check confirmation popup -->
  <div
    v-if="showDeleteCheckConfirm"
    class="confirm-popup delete-check-confirm"
    :style="{
      position: 'fixed',
      top: deleteCheckPos.y + 'px',
      left: deleteCheckPos.x + 'px',
      transform: 'none',
    }"
  >
    <div class="confirm-message">Delete this check?</div>
    <div class="confirm-buttons">
      <button @click="confirmDeleteCheck">Yes</button>
      <button @click="showDeleteCheckConfirm = false">No</button>
    </div>
  </div>

  <ManageAddress
    :id="selectedRow?.Id"
    :name="selectedRow?.Name"
    :showModal="showAddressModal"
    @close="showAddressModal = false"
  />
</template>

<script>
import axios from '@/axiosConfig';
import debounce from 'lodash/debounce';
import LedgerEntry from './LedgerEntry.vue'; // Import the dialog component
import ManageAddress from './ManageAddress.vue'; // Import the dialog component
import eventBus from './eventBus.js';
import io from 'socket.io-client';
import { isUnmatchedTaggedLedgerRow } from '@/utils/ledgerTaggedFilter';
import {
  nextCheckNumber,
  printPlaceholderEntries,
  ledgerCheckNumberUpdate,
  applyAssignedCheckNumber,
} from '@/utils/printCheckNumber';

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default {
  emits: [
    'rowSelected',
    'update-show-split-details',
    'selectedLedgerRow',
    'selectedLedgerRows',
    'requestShowShortcuts',
    'requestHandleMatch',
    'update-date-range',
  ],
  components: {
    LedgerEntry,
    ManageAddress,
  },
  props: {
    selectedBankTransaction: {
      type: Object,
      required: true,
      default: () => ({
        ledgerDate: '',
        BankRefDateTime: '',
        BankType: '',
        BankRefCheckNum: '',
        BankRefNum: '',
        BankRefDesc: '',
        Credit: '0.00',
        Debit: '0.00',
        Balance: '0.00',
        Type: '',
      }),
    },

    searchValue: {
      // New prop to receive search value from parent
      type: String,
      default: '',
    },

    ledgerFromDateProp: {
      // Date range from parent (MainPage)
      type: String,
      default: '',
    },

    ledgerToDateProp: {
      // Date range to parent (MainPage)
      type: String,
      default: '',
    },
  },
  data() {
    // calculate the date selectors for the bank ledger
    let todaysDate = new Date().toISOString().substr(0, 10); // Today's date in YYYY-MM-DD format
    let lookbackDate = new Date();
    lookbackDate.setMonth(lookbackDate.getMonth() - 1); // Move to the previous month
    lookbackDate.setDate(1); // Set the day to the first day of that month
    lookbackDate = lookbackDate.toISOString().substr(0, 10); // Format as YYYY-MM-DD

    return {
      //
      pdfUrl: null,
      showPDF: false,
      viewerTitle: 'check viewer',
      isDragMode: false,
      // New properties
      isPrinterFriendly: false,
      noWrapText: false,
      suppressRightColumns: false,
      footerMessage: '',
      footerMessageTimeout: null,
      filteredTransactionsData: [], // Add a new data property
      currentDate: new Date(),
      accountOptions: [],
      selectedAccount: null,
      selectedAccountName: null,
      showEditDialog: false, // Control dialog visibility
      selectedRow: null,
      hovered: false, // Track if cursor is over the table
      splitsTotal: 0,
      showSplitsInTable: false,
      clickTimeout: null, // To track single vs double-click
      selectedRowIds: [],
      // While set, socket/fetch rebinds select this ledger id (post-save).
      pendingSelectLedgerId: null,
      taggedIds: JSON.parse(localStorage.getItem('qb_taggedIds') || '[]'),
      filterTaggedMode: false,
      searchQuery: '',
      rawSearchQuery: '',
      transactions: [],
      splitDetails: [],
      showSplitDetails: false,
      earliestDate: '',
      totalRows: 0,
      ledgerToDate: todaysDate,
      ledgerFromDate: lookbackDate,
      headers: [
        'Date',
        'Type',
        'Num',
        'Name',
        'Amount',
        'Memo',
        'Account',
        'Split/Bank Acct',
        'Class',
        'T',
        'M',
        'V',
        'x',
        'D',
      ],
      selectedYear:
        new Date().getMonth() < 2
          ? new Date().getFullYear() - 1
          : new Date().getFullYear(),
      monthShortcuts: [
        { short: 'J', name: 'January' },
        { short: 'F', name: 'February' },
        { short: 'M', name: 'March' },
        { short: 'A', name: 'April' },
        { short: 'M', name: 'May' },
        { short: 'J', name: 'June' },
        { short: 'J', name: 'July' },
        { short: 'A', name: 'August' },
        { short: 'S', name: 'September' },
        { short: 'O', name: 'October' },
        { short: 'N', name: 'November' },
        { short: 'D', name: 'December' },
      ],
      debounceTimeout: 300,
      isNew: false,
      socket: null,
      showAddressModal: false,
      showDeletedRows: false,
      showDeleteCheckConfirm: false,
      deleteCheckPos: { x: 0, y: 0 },
      mousePos: { x: 0, y: 0 },
      newLedgerRow: null,
      noDoubleEntry: true,
      selectedBankRow: null,
      findUnmatchedMode: false,
      filterUtilitiesMode: false,
      // Sorting state for Num column
      numSortOrder: 'none', // 'none', 'desc', 'asc'
      // ID to highlight after filtering
      highlightRowId: null,
      // Share functionality
      showSharePopup: false,
      shareUrl: '',
    };
  },
  computed: {
    parsedEarliestDate() {
      return this.earliestDate ? new Date(this.earliestDate) : new Date();
    },

    filteredTransactions() {
      let filteredData = this.transactions;

      // Check if we're doing an ID-based search
      const isIdSearch = this.searchQuery && this.searchQuery.includes(':id=');

      // Apply account filter only if not doing ID-based search
      if (!isIdSearch) {
        filteredData = filteredData.filter((transaction) => {
          return (
            (transaction.Account &&
              transaction.Account.includes(this.selectedAccountName)) ||
            (transaction.Split &&
              transaction.Split.includes(this.selectedAccountName))
          );
        });
      }

      // Skip other filters during ID search
      if (!isIdSearch) {
        if (this.noDoubleEntry) {
          filteredData = filteredData.filter((transaction) => {
            return (
              transaction.Amount >= 0 || transaction.Split.includes('-SPLIT-')
            );
          });
        }

        // Apply tagged filter when mode is active
        if (this.filterTaggedMode) {
          filteredData = filteredData.filter((transaction) =>
            isUnmatchedTaggedLedgerRow(
              transaction,
              this.taggedIds,
              this.ledgerFromDate,
              this.ledgerToDate,
            ),
          );
        }

        // Apply utilities filter when mode is active
        if (this.filterUtilitiesMode) {
          filteredData = filteredData.filter((transaction) => {
            return (
              (transaction.Account &&
                transaction.Account.toLowerCase().includes('utilities')) ||
              (transaction.Split &&
                transaction.Split.toLowerCase().includes('utilities'))
            );
          });
        }

        // Apply unmatched filter when mode is active
        if (this.findUnmatchedMode) {
          filteredData = filteredData.filter((transaction) => {
            let amount = parseFloat(transaction.Amount);
            const isCheckNumNumeric = !isNaN(parseInt(transaction.CheckNum));
            const typeLower = transaction.Type?.toLowerCase();
            return (
              (typeLower?.includes('check') || typeLower === 'debit') &&
              transaction.CheckNum != null &&
              isCheckNumNumeric &&
              !isNaN(amount) &&
              transaction.Voided === 0 &&
              (transaction.Split?.includes(this.selectedAccountName) ||
                transaction.Split?.includes('-SPLIT-')) &&
              transaction.Matched === 0
            );
          });
        }
      }

      // Apply sorting if numSortOrder is not 'none' (before search query check)
      if (this.numSortOrder !== 'none') {
        filteredData = filteredData.sort((a, b) => {
          const aNum = parseInt(a.CheckNum) || 0;
          const bNum = parseInt(b.CheckNum) || 0;

          if (this.numSortOrder === 'desc') {
            return bNum - aNum;
          } else {
            return aNum - bNum;
          }
        });
      }

      if (!this.searchQuery) {
        return filteredData;
      }

      // Split the search query by comma and trim whitespace
      const queries = this.searchQuery.split(',').map((query) => query.trim());
      let invertSearch = false;

      queries.forEach((query) => {
        invertSearch = false;
        if (query.startsWith('!')) {
          invertSearch = true;
          query = query.substring(1).trim(); // Remove '!' and trim the query
        }
        if (query.startsWith(':void')) {
          filteredData = filteredData.filter((transaction) => {
            return transaction.Voided === 1;
          });
        } else if (query.startsWith(':verify')) {
          // Filter for unmatched and non-voided transactions
          filteredData = filteredData.filter((transaction) => {
            return transaction.Matched === 0 && transaction.Voided === 0;
          });
        } else if (query.startsWith(':m')) {
          // Filter for matched only
          filteredData = filteredData.filter((transaction) => {
            return transaction.Matched === 1;
          });
        } else if (query.startsWith(':!m')) {
          // Filter for unmatched only
          filteredData = filteredData.filter((transaction) => {
            return transaction.Matched === 0;
          });
        } else if (query.startsWith(':t')) {
          // Filter for today only
          this.displayFooterMessage('Filtering for today only');

          // Get today's date in "MM/DD/YY" format
          const today = new Date();
          const formattedToday =
            ('0' + (today.getMonth() + 1)).slice(-2) +
            '/' +
            ('0' + today.getDate()).slice(-2) +
            '/' +
            today.getFullYear().toString().slice(-2);

          // default to :a
          filteredData = filteredData.filter((transaction) => {
            return transaction.Account.includes(this.selectedAccountName);
          });

          filteredData = filteredData.filter((transaction) => {
            return transaction.Date === formattedToday;
          });
        } else if (query.startsWith(':f')) {
          // Filter for future transactions
          this.displayFooterMessage(
            'Filtering for futured dated transactions only',
          );
          const now = new Date();
          filteredData = filteredData.filter((transaction) => {
            const transactionDateParts = transaction.Date.split('/');
            const transactionDate = new Date(
              `20${transactionDateParts[2]}`, // Year
              transactionDateParts[0] - 1, // Month (0-based index)
              transactionDateParts[1], // Day
            );
            return transactionDate > now;
          });
        } else if (query.startsWith(':id=')) {
          // Filter by specific ID and enable split view
          const targetId = parseInt(query.substring(4)); // Remove ':id=' prefix
          if (targetId) {
            filteredData = filteredData.filter((transaction) => {
              const isMatch =
                transaction.Id === targetId ||
                transaction.LinkedSplitId === targetId;
              return isMatch;
            });

            this.displayFooterMessage(
              `Filtering for transaction ID: ${targetId} and its splits`,
            );

            // Enable split view for this transaction and refetch data to include children
            if (!this.showSplitsInTable) {
              this.showSplitsInTable = true;
              // Trigger a data refresh to include child transactions
              this.fetchTransactions();
              return filteredData; // Return current data while refresh is in progress
            }
          }
        } else {
          if (invertSearch) {
            this.displayFooterMessage('inverting filter');
            filteredData = filteredData.filter(
              (transaction) =>
                !Object.values(transaction).some(
                  (value) =>
                    value != null &&
                    value
                      .toString()
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                ),
            );
          } else {
            filteredData = filteredData.filter((transaction) =>
              Object.values(transaction).some(
                (value) =>
                  value != null &&
                  value.toString().toLowerCase().includes(query.toLowerCase()),
              ),
            );
          }
        }
      });

      // Removed automatic row selection logic from here
      return filteredData;
    },

    visibleTransactionCount() {
      return this.filteredTransactions.length;
    },

    selectedRowTotals() {
      let totalCredits = 0;
      let totalDebits = 0;
      let workingAmount = 0;

      this.selectedRowIds.forEach((id) => {
        const transaction = this.transactions.find((t) => t.Id === id);
        if (transaction) {
          if (transaction.Split?.includes('-SPLIT-')) {
            workingAmount = -transaction.Amount;
          } else {
            workingAmount = transaction.Amount;
          }
          if (workingAmount > 0) {
            totalCredits += parseFloat(workingAmount);
          } else if (transaction.Amount < 0) {
            totalDebits += parseFloat(transaction.Amount);
          }
        }
      });

      return {
        count: this.selectedRowIds.length,
        totalCredits: totalCredits.toFixed(2),
        totalDebits: totalDebits.toFixed(2),
      };
    },
  },

  methods: {
    // Format date from YYYY-MM-DD to MM/DD/YY
    formatDateShort(dateString) {
      if (!dateString) return '';

      // Parse the date string manually to avoid timezone issues
      const parts = dateString.split('-');
      if (parts.length !== 3) return '';

      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const day = parseInt(parts[2]);

      if (isNaN(year) || isNaN(month) || isNaN(day)) return '';

      const monthStr = String(month).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const yearStr = String(year).slice(-2);

      return `${monthStr}/${dayStr}/${yearStr}`;
    },

    // Parse MM/DD/YY or MM/DD/YYYY to YYYY-MM-DD
    parseDateShort(dateString) {
      if (!dateString) return '';

      const parts = dateString.split('/');
      if (parts.length !== 3) return '';

      let [month, day, year] = parts;

      // Convert 2-digit year to 4-digit
      if (year.length === 2) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        year = currentCentury + parseInt(year);

        // If the resulting year is more than 50 years in the future, assume previous century
        if (year > currentYear + 50) {
          year -= 100;
        }
      }

      // Pad month and day with zeros if needed
      month = month.padStart(2, '0');
      day = day.padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    // Update from date
    updateFromDate(event) {
      const inputValue = event.target.value;
      this.tempFromDate = inputValue;
    },

    // Update to date
    updateToDate(event) {
      const inputValue = event.target.value;
      this.tempToDate = inputValue;
    },

    // Validate and set from date
    validateFromDate(event) {
      const inputValue = event.target.value;
      const parsedDate = this.parseDateShort(inputValue);
      if (parsedDate) {
        this.ledgerFromDate = parsedDate;
        this.debouncedfetchTransactions();
      }
    },

    // Validate and set to date
    validateToDate(event) {
      const inputValue = event.target.value;
      const parsedDate = this.parseDateShort(inputValue);
      if (parsedDate) {
        this.ledgerToDate = parsedDate;
        this.debouncedfetchTransactions();
      }
    },

    // Set date range for a specific month
    setMonthRange(monthName) {
      const year = this.selectedYear;
      const monthIndex = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ].indexOf(monthName);

      if (monthIndex === -1) return;

      // First day of the month
      const firstDay = new Date(year, monthIndex, 1);
      // Last day of the month
      const lastDay = new Date(year, monthIndex + 1, 0);

      // Format to YYYY-MM-DD
      this.ledgerFromDate = firstDay.toISOString().substr(0, 10);
      this.ledgerToDate = lastDay.toISOString().substr(0, 10);

      // Emit to parent so both tables stay in sync
      this.$emit('update-date-range', {
        from: this.ledgerFromDate,
        to: this.ledgerToDate,
        selectedYear: this.selectedYear,
      });

      // Refresh transactions
      this.debouncedfetchTransactions();

      // Show feedback message
      this.displayFooterMessage(`Date range set to ${monthName} ${year}`);
    },

    setYearRange(year) {
      this.selectedYear = year;
      this.ledgerFromDate = `${year}-01-01`;
      this.ledgerToDate = `${year}-12-31`;
      this.$emit('update-date-range', {
        from: this.ledgerFromDate,
        to: this.ledgerToDate,
        selectedYear: year,
      });
      this.debouncedfetchTransactions();
      this.displayFooterMessage(`Date range set to ${year}`);
    },

    // Share current search functionality
    shareCurrentSearch() {
      const params = new URLSearchParams();

      // Add page parameter to indicate this is a ledger search
      params.append('page', 'ledger');

      if (this.searchQuery) {
        params.append('q', this.searchQuery);
      }
      if (this.ledgerFromDate) {
        params.append('start', this.ledgerFromDate);
      }
      if (this.ledgerToDate) {
        params.append('end', this.ledgerToDate);
      }
      if (this.selectedAccountName) {
        params.append('account', this.selectedAccountName);
      }

      // Use current page URL as base, removing any existing query parameters
      const currentUrl = new URL(window.location.href);
      currentUrl.search = ''; // Clear existing query parameters
      const baseUrl = currentUrl.toString();

      this.shareUrl = `${baseUrl}?${params.toString()}`;
      this.showSharePopup = true;
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        // Provide feedback that the URL was copied
        const originalText = event.target.textContent;
        event.target.textContent = 'Copied!';
        setTimeout(() => {
          event.target.textContent = originalText;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    },

    // Toggle sorting for Num column
    toggleNumSort() {
      if (this.numSortOrder === 'none') {
        this.numSortOrder = 'desc';
      } else if (this.numSortOrder === 'desc') {
        this.numSortOrder = 'asc';
      } else {
        this.numSortOrder = 'none';
      }
    },

    showDeleted() {
      this.showDeletedRows = !this.showDeletedRows;
      this.fetchTransactions();
    },

    handleDoubleClick(event) {
      // console.log('double click event', event);
      event.stopPropagation(); // Prevent propagation to other listeners
      if (this.$refs.pdfViewer && this.$refs.pdfViewer.contains(event.target)) {
        const rect = this.$refs.pdfViewer.getBoundingClientRect();
        const x = event.clientX - rect.left; // x position within the element
        const y = event.clientY - rect.top; // y position within the element

        // Define the top left corner coordinates of the specific rectangle area
        const rectX1 = 100;
        const rectY1 = 260;
        const rectWidth = 180;
        const rectHeight = 70;

        // Check if the click is within the specified rectangle
        if (
          x >= rectX1 &&
          x <= rectX1 + rectWidth &&
          y >= rectY1 &&
          y <= rectY1 + rectHeight
        ) {
          // Display a dialog to collect address information
          this.showAddressModal = true;
        }
      }
    },

    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'docuScan',
          },
        });

        this.socket.on('connect', () => {
          console.log('QBpage: Connected to WebSocket server');
        });

        /*
        this.socket.onAny((event, ...args) => {
          console.log(`Received event: ${event}`, args);
        });
        */

        this.socket.on('ledgerDataChanged', (data) => {
          if (data === null) {
            return;
          }

          data.Amount = parseFloat(data.Amount);
          // console.log('changed Data: ', data);

          const requiredProperties = [
            'Date',
            'Type',
            'CheckNum',
            'Name',
            'Amount',
            'Memo',
            'Account',
            'Split',
            'Class',
            'Matched',
            'Voided',
            'LinkedTransactionId',
            'Id',
            'LinkedSplitId',
            'Deleted',
            'linkedDocId',
          ];

          // Ensure all required properties are present and remove extraneous properties.
          // Use nullish coalescing so 0 / falsy amounts and ids are preserved.
          const sanitizedData = {};
          requiredProperties.forEach((prop) => {
            sanitizedData[prop] =
              data[prop] !== undefined && data[prop] !== null
                ? data[prop]
                : null;
          });

          const index = this.transactions.findIndex((transaction) =>
            this.ledgerIdsEqual(transaction.Id, sanitizedData.Id),
          );

          // console.log('sanitizedData[Amount]: ', sanitizedData['Amount']);
          if (index >= 0) {
            // sanitizedData.Amount = this.formatNumber(sanitizedData.Amount);
            this.transactions[index] = sanitizedData;
          } else {
            // Find the correct position based on date
            const dateIndex = this.transactions.findIndex(
              (transaction) =>
                new Date(transaction.Date) < new Date(sanitizedData.Date),
            );
            if (dateIndex === -1) {
              this.transactions.push(sanitizedData);
            } else {
              this.transactions.splice(dateIndex, 0, sanitizedData);
            }
          }
          // After save, force-select the saved id even if another row was selected
          if (
            this.pendingSelectLedgerId != null &&
            this.ledgerIdsEqual(sanitizedData.Id, this.pendingSelectLedgerId)
          ) {
            this.selectedRowIds = [sanitizedData.Id];
            this.selectedRow = sanitizedData;
          } else if (this.selectedRowIds.length === 0) {
            // Only auto-select a newly inserted row if nothing was selected
            if (index < 0) {
              this.selectedRowIds = [sanitizedData.Id];
            }
          }
          // Rebind selection to the new object refs so MainPage link checks
          // use current linkedDocId (not a stale pre-replace row object).
          this.refreshSelectedRowReferences();
        });

        this.socket.on('ledgerDataDeleted', ({ id }) => {
          // console.log('ledger data deleted:', id);
          const index = this.transactions.findIndex(
            (transaction) => transaction.Id === id,
          );
          if (index >= 0) {
            this.transactions.splice(index, 1);
          }
        });

        this.socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },
    async initializeData() {
      await this.getDate2();
      await this.fetchAccounts();
      this.fetchTransactions();
      this.fetchTransactionInfo();
    },

    applyFilter() {},

    displayFooterMessage(message) {
      this.footerMessage = message;

      // Clear any existing timeouts
      if (this.footerMessageTimeout) {
        clearTimeout(this.footerMessageTimeout);
      }

      // Set a timeout to clear the message after 3 seconds
      this.footerMessageTimeout = setTimeout(() => {
        // Apply fading effect
        const footerMessageDiv = document.querySelector('.footer-message');
        if (footerMessageDiv) {
          footerMessageDiv.classList.add('fade-out');
          setTimeout(() => {
            this.footerMessage = '';
            footerMessageDiv.classList.remove('fade-out');
          }, 1000); // The duration of the fade-out effect
        }
      }, 3000);
    },

    isBoundaryRow(index) {
      const futureTransactionIndex = this.transactions.findIndex(
        (transaction) => new Date(transaction.Date) < this.currentDate,
      );
      return index === futureTransactionIndex;
    },

    async getDate2() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/qbtransactions/info', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = response.data;
        this.ledgerToDate = new Date(result[0].lastDate)
          .toISOString()
          .split('T')[0];
      } catch (error) {
        this.logMessages += 'Failed to load accounts.\n';
        console.error('Error fetching accounts:', error);
      }
    },

    async handleAccountChange() {
      const selectedAccount = this.accountOptions.find(
        (account) => account.accountNumber === this.selectedAccount,
      );
      if (selectedAccount) {
        this.selectedAccountName = selectedAccount.accountName;

        // Clear search filter
        this.searchQuery = '';
        this.rawSearchQuery = '';

        // Reset various mode flags
        this.showSplitsInTable = false;
        this.noDoubleEntry = false;
        this.findUnmatchedMode = false;

        // Clear selection
        this.clearSelection();

        // Fetch transactions first with a very wide date range to get all data
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const futureDate = new Date(today.getFullYear() + 1, 11, 31); // End of next year to capture future transactions
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Set a very wide date range to get all transactions for this account, including future-dated ones
        this.ledgerFromDate = formatDate(startOfYear);
        this.ledgerToDate = formatDate(futureDate);

        // Fetch transactions with new settings
        await this.fetchTransactions();

        // Now reset date range with the fresh transaction data
        await this.resetDateRange();
      }
    },

    async fetchAccounts() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('api/get-bank-accounts', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.accountOptions = response.data;

        // Check if account was set from URL params
        const urlParams = new URLSearchParams(window.location.search);
        let accountParam = urlParams.get('account');

        if (accountParam) {
          // Decode + signs to spaces (URLSearchParams doesn't handle this automatically)
          accountParam = accountParam.replace(/\+/g, ' ');

          // Find and set the account based on URL param
          const account = this.accountOptions.find(
            (acc) => acc.accountName === accountParam,
          );
          if (account) {
            this.selectedAccount = account.accountNumber;
            this.selectedAccountName = account.accountName;
          } else if (this.accountOptions.length > 0) {
            // Fallback to first account if URL param account not found
            this.selectedAccount = this.accountOptions[0].accountNumber;
            this.selectedAccountName = this.accountOptions[0].accountName;
          }
        } else if (this.accountOptions.length > 0) {
          // Default to first account if no URL param
          this.selectedAccount = this.accountOptions[0].accountNumber;
          this.selectedAccountName = this.accountOptions[0].accountName;
        }
      } catch (error) {
        this.logMessages += 'Failed to load accounts.\n';
        console.error('Error fetching accounts:', error);
      }
    },

    confirmAccount() {
      this.isAccountConfirmed = true;
      this.noDoubleEntry = !this.noDoubleEntry;
    },

    showSplits() {
      this.findUnmatchedMode = false;
      this.showSplitsInTable = !this.showSplitsInTable;
      this.showSplitDetails = false;
      this.noDoubleEntry = false;
      this.fetchTransactions().then(() => {
        // Scroll the table to the selected row
        if (this.selectedRow) {
          this.$nextTick(() => {
            const selectedRowElement = document.querySelector(
              `#row-${this.selectedRow.Id}`,
            );
            if (selectedRowElement) {
              selectedRowElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start', // Adjust to 'center' to ensure visibility
              });
            }
          });
        }
      });
    },

    stripId(transaction) {
      const temp = { ...transaction };
      delete temp.Id;
      delete temp.LinkedSplitId;
      delete temp.LinkedTransactionId;
      delete temp.qbTransactionNum;
      delete temp.IsSplitParent;
      // Insert Tagged before Matched to align with 'T' header position
      const result = {};
      for (const key of Object.keys(temp)) {
        if (key === 'Matched') {
          result.Tagged = this.taggedIds.some((tid) =>
            this.ledgerIdsEqual(tid, transaction.Id),
          )
            ? 1
            : 0;
        }
        result[key] = temp[key];
      }
      return result;
    },

    async fetchTransactionInfo() {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get('/api/qbtransactions/info', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.earliestDate = response.data[0].earliestDate;
        this.totalRows = response.data[0].totalRows;
        // this.date1 = new Date(this.earliestDate).toISOString().split('T')[0]
        // this.date2 = new Date().toISOString().split('T')[0]
      } catch (error) {
        console.error('Error fetching qb transaction info:', error);
      }
    },

    // make a debounced version of fetchTransactions with a 1000ms delay
    debouncedfetchTransactions: debounce(function () {
      this.fetchTransactions();
    }, 700),

    async fetchTransactions() {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get('/api/qbtransactions', {
          params: {
            date1: this.ledgerFromDate,
            date2: this.ledgerToDate,
            includeLinkedSplitIds: this.showSplitsInTable,
            includeDeleted: this.showDeletedRows,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.transactions = response.data;
        this.refreshSelectedRowReferences();
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          console.error('Error fetching transactions:', error);
        }
      }
    },

    /**
     * Notify MainPage of ledger selection.
     * openLinkedDocument: only true for intentional user navigation (click/arrows).
     * Programmatic selection (save, fetch, socket) must not replace the open doc.
     */
    emitLedgerRowSelected(row, { openLinkedDocument = false } = {}) {
      eventBus.emit('ledgerRowSelected', { row, openLinkedDocument });
    },

    /** NavBar mounts Cash Position with class cash-position-wrapper (v-if). */
    isCashPositionVisible() {
      return !!document.querySelector('.cash-position-wrapper');
    },

    /**
     * User-driven table selection (click / arrows): open linked document and close
     * NavBar tool panels only when Cash Position is not open. While Cash Position
     * is visible, keep it and do not switch to the document viewer.
     */
    notifyUserLedgerSelection(row) {
      const cashOpen = this.isCashPositionVisible();
      this.emitLedgerRowSelected(row, { openLinkedDocument: !cashOpen });
      if (!cashOpen) {
        eventBus.emit('closeNavBarPanels');
      }
    },

    ledgerIdsEqual(a, b) {
      return a != null && b != null && Number(a) === Number(b);
    },

    findTransactionById(id) {
      const n = Number(id);
      if (!n) return null;
      return this.transactions.find((t) => Number(t.Id) === n) || null;
    },

    /**
     * After transactions are replaced (fetch / socket), rebind selectedRow and
     * re-emit selection so consumers hold current object refs (linkedDocId etc.).
     */
    refreshSelectedRowReferences() {
      if (!this.selectedRowIds?.length) {
        return;
      }
      // Prefer a row we are mid-selecting after save (socket may race with find)
      if (this.pendingSelectLedgerId != null) {
        const pending = this.findTransactionById(this.pendingSelectLedgerId);
        if (pending) {
          this.selectedRow = pending;
          this.selectedRowIds = [pending.Id];
          this.$emit('selectedLedgerRow', pending);
          this.$emit('selectedLedgerRows', [pending]);
          this.emitLedgerRowSelected(pending, { openLinkedDocument: false });
          return;
        }
      }
      const selectedRows = this.transactions.filter((t) =>
        this.selectedRowIds.some((id) => this.ledgerIdsEqual(id, t.Id)),
      );
      // Drop ids that no longer exist in the loaded set
      if (selectedRows.length !== this.selectedRowIds.length) {
        this.selectedRowIds = selectedRows.map((t) => t.Id);
      }
      if (selectedRows.length === 0) {
        this.selectedRow = null;
        this.$emit('selectedLedgerRows', []);
        this.emitLedgerRowSelected(null, { openLinkedDocument: false });
        return;
      }
      if (selectedRows.length === 1) {
        this.selectedRow = selectedRows[0];
        this.$emit('selectedLedgerRow', this.selectedRow);
        this.$emit('selectedLedgerRows', selectedRows);
        this.emitLedgerRowSelected(this.selectedRow, {
          openLinkedDocument: false,
        });
      } else {
        if (
          this.selectedRow &&
          !selectedRows.some((r) => this.ledgerIdsEqual(r.Id, this.selectedRow.Id))
        ) {
          this.selectedRow = selectedRows[0];
        } else if (this.selectedRow) {
          const fresh = selectedRows.find((r) =>
            this.ledgerIdsEqual(r.Id, this.selectedRow.Id),
          );
          if (fresh) this.selectedRow = fresh;
        }
        this.$emit('selectedLedgerRows', selectedRows);
      }
    },

    debounceSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.searchQuery = this.searchQuery.trim();
      }, 300);
    },

    searchByName(name) {
      if (name == null) return;
      const value = String(name).trim();
      if (!value) return;
      this.rawSearchQuery = value;
      this.searchQuery = value;
    },

    clearSearch() {
      this.searchQuery = '';
      this.rawSearchQuery = '';
      this.findUnmatchedMode = false;
      // Remove the 'selected-row' class from all rows
      const selectedRows = document.querySelectorAll('.selected-row-scroll');
      selectedRows.forEach((row) =>
        row.classList.remove('selected-row-scroll'),
      );
    },

    focusSearch() {
      if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
      this.$nextTick(() => {
        this.$refs.searchQuery.select();
      });
    },

    expandSearch() {
      // Same as clicking the account filter icon: show double-entry rows (e.g. TRXF)
      this.noDoubleEntry = false;

      const currentYear = new Date().getFullYear();

      // First day of the current year
      let fromDate = new Date(currentYear, 0, 1);

      // Check if fromDate is already the first day of the current year
      if (this.ledgerFromDate === fromDate.toISOString().split('T')[0]) {
        // Set fromDate to 365 days earlier
        fromDate.setDate(fromDate.getDate() - 365);
      }

      // Last day of the current year
      const toDate = new Date(currentYear, 11, 31);

      // Format the dates to 'YYYY-MM-DD' format
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      this.ledgerFromDate = formatDate(fromDate);
      this.ledgerToDate = formatDate(toDate);
      this.fetchTransactions();
    },

    async resetDateRange(option) {
      const today = new Date();

      if (option === 'ytd') {
        // Use string-based date construction to avoid timezone issues
        const currentYear = today.getFullYear();
        const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
        const currentDay = String(today.getDate()).padStart(2, '0');

        // Set start date to January 1st of current year
        this.ledgerFromDate = `${currentYear}-01-01`;
        // Set end date to today
        this.ledgerToDate = `${currentYear}-${currentMonth}-${currentDay}`;

        this.fetchTransactions();
        this.displayFooterMessage(
          `Date range set to year-to-date: Jan 1, ${currentYear} to today`,
        );
        return;
      }

      // Default behavior - use smart date range based on latest transaction for selected account
      // Use string-based date construction to avoid timezone issues
      const currentYear = today.getFullYear();

      // Set start date to January 1st of current year
      const startOfYearStr = `${currentYear}-01-01`;

      // First, fetch transactions with a wide date range to ensure we get all transactions
      const futureDate = new Date(today.getFullYear() + 1, 11, 31); // End of next year to capture future transactions
      const formatDateForRange = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Temporarily set a wide date range to fetch all transactions
      this.ledgerFromDate = startOfYearStr;
      this.ledgerToDate = formatDateForRange(futureDate);

      // Fetch all transactions with the wide range
      await this.fetchTransactions();

      // Find the latest transaction date for the selected account
      let latestDate = today; // Default to today if no transactions
      if (
        this.transactions &&
        this.transactions.length > 0 &&
        this.selectedAccountName
      ) {
        // Filter transactions for the selected account and parse their dates
        const accountTransactionDates = this.transactions
          .filter(
            (transaction) =>
              (transaction.Account &&
                transaction.Account.includes(this.selectedAccountName)) ||
              (transaction.Split &&
                transaction.Split.includes(this.selectedAccountName)),
          )
          .map((transaction) => {
            if (!transaction.Date) return null;
            // Convert MM/DD/YY format to Date object
            const dateParts = transaction.Date.split('/');
            if (dateParts.length === 3) {
              const month = parseInt(dateParts[0], 10) - 1; // Month is 0-based
              const day = parseInt(dateParts[1], 10);
              let year = parseInt(dateParts[2], 10);
              // Handle 2-digit years - assume 20xx for years 00-99
              if (year < 100) {
                year += 2000;
              }
              return new Date(year, month, day);
            }
            return null;
          })
          .filter((date) => date !== null);

        if (accountTransactionDates.length > 0) {
          // Find the actual latest date (could be future-dated)
          latestDate = new Date(Math.max(...accountTransactionDates));
        }
      }

      // Calculate start date as 60 days before latest transaction date
      const startDate = new Date(latestDate);
      startDate.setDate(latestDate.getDate() - 60);

      // Set the final date range: 60 days before latest transaction to latest transaction
      this.ledgerFromDate = formatDateForRange(startDate);
      this.ledgerToDate = formatDateForRange(latestDate);
      await this.fetchTransactions();

      const latestDateStr = latestDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const startDateStr = startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      this.displayFooterMessage(
        `Date range set: ${startDateStr} to ${latestDateStr} (60 days before latest transaction)`,
      );
    },

    async resetDateRangeToYTD() {
      // Use string-based date construction to avoid timezone issues
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
      const currentDay = String(today.getDate()).padStart(2, '0');

      // Set start date to January 1st of current year
      this.ledgerFromDate = `${currentYear}-01-01`;
      // Set end date to today
      this.ledgerToDate = `${currentYear}-${currentMonth}-${currentDay}`;

      this.fetchTransactions();
    },

    clearSelection() {
      this.selectedRowIds = [];
      this.$emit('selectedLedgerRows', []);
    },

    highlightSearch(value) {
      if (!this.searchQuery) return value;

      // Only highlight if results < 30
      if (this.filteredTransactions.length >= 30) return value;

      // Split search query by comma and get the last token
      const queries = this.searchQuery.split(',').map((query) => query.trim());
      const lastQuery = queries[queries.length - 1];

      // Only highlight if search term length > 2
      if (!lastQuery || lastQuery.length <= 2) return value;

      // Skip highlighting for special commands that start with :
      if (lastQuery.startsWith(':')) return value;

      const escapedQuery = escapeRegExp(lastQuery);
      const searchRegex = new RegExp(`(${escapedQuery})`, 'gi');
      return value != null
        ? value
            .toString()
            .replace(searchRegex, '<span class="highlight">$1</span>')
        : value;
    },

    handleRowClick(transaction, event) {
      // console.log('handleRowClick called for transaction:', transaction.Id);
      clearTimeout(this.clickTimeout); // Clear any existing timeout

      this.clickTimeout = setTimeout(() => {
        // console.log('Inside clickTimeout for transaction:', transaction.Id);
        this.selectedRow = transaction; // Set selected row

        const transactionId = transaction.Id;
        if (event.ctrlKey || event.metaKey) {
          // CTRL-click: only toggle selection, don't show linked doc or close panels
          this.toggleSelection(transactionId);
        } else if (event.shiftKey) {
          this.selectRange(transactionId);
        } else {
          this.$emit('rowSelected', transaction);
          this.$emit('selectedLedgerRow', this.selectedRow);
          this.notifyUserLedgerSelection(this.selectedRow);
          this.singleSelect(transactionId);
        }
      }, 200); // Delay to distinguish from double-click, adjust as needed
    },

    async handleRowDoubleClick(transaction, event) {
      const clickedElement = event.target;
      clearTimeout(this.clickTimeout); // Clear the single-click timeout
      if (clickedElement.classList.contains('voided')) {
        this.voidTransaction(transaction);
        return;
      }
      if (transaction.Split === '-SPLIT-') {
        this.highlightedRowId = transaction.Id;
        this.fetchSplitDetails(transaction.Id);
        if (this.selectedRow?.Id !== transaction.Id) {
          this.singleSelect(transaction.Id);
        }
        this.selectedRow = transaction;
        this.splitsTotal = transaction.Amount;
        if (event.ctrlKey) {
          this.printCheck('preview');
        } else {
          this.showEditDialog = true;
        }
      } else {
        this.highlightedRowId = transaction.Id;
        if (this.selectedRow?.Id !== transaction.Id) {
          this.singleSelect(transaction.Id);
        }
        this.selectedRow = transaction;
        if (event.ctrlKey) {
          this.printCheck('preview');
        } else {
          this.showEditDialog = true;
        }
      }
    },

    toggleSelection(transactionId) {
      const index = this.selectedRowIds.indexOf(transactionId);
      if (index > -1) {
        this.selectedRowIds.splice(index, 1);
      } else {
        this.selectedRowIds.push(transactionId);
        // Emit the full list of selected rows
        const selectedRows = this.filteredTransactions.filter((transaction) =>
          this.selectedRowIds.includes(transaction.Id),
        );
        this.$emit('selectedLedgerRows', selectedRows);
      }
    },

    selectRange(transactionId) {
      if (this.selectedRowIds.length === 0) {
        this.selectedRowIds.push(transactionId);
      } else {
        const lastSelectedId =
          this.selectedRowIds[this.selectedRowIds.length - 1];

        // Use optional chaining to avoid errors if transaction is null or undefined
        const startIndex = this.filteredTransactions.findIndex(
          (transaction) => transaction?.Id === lastSelectedId,
        );
        const endIndex = this.filteredTransactions.findIndex(
          (transaction) => transaction?.Id === transactionId,
        );

        if (startIndex !== -1 && endIndex !== -1) {
          // Check if both indices are valid
          const [start, end] =
            startIndex < endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex];

          for (let i = start; i <= end; i++) {
            const id = this.filteredTransactions[i]?.Id; // Optional chaining for safety
            if (id && !this.selectedRowIds.includes(id)) {
              this.selectedRowIds.push(id);
            }
          }
        }
      }

      // Emit the full list of selected rows with a null/undefined check
      const selectedRows = this.filteredTransactions.filter(
        (transaction) =>
          transaction?.Id && this.selectedRowIds.includes(transaction.Id),
      );

      // Emit the range of filteredTransactions that match these Ids
      this.$emit('selectedLedgerRows', selectedRows);
    },

    singleSelect(transactionId) {
      if (
        this.selectedRowIds.length === 1 &&
        this.selectedRowIds[0] === transactionId
      ) {
        this.selectedRowIds = [];
      } else {
        this.selectedRowIds = [transactionId];
      }
      const selectedRows = this.filteredTransactions.filter((transaction) =>
        this.selectedRowIds.includes(transaction.Id),
      );
      this.$emit('selectedLedgerRows', selectedRows);
    },

    isSelected(transactionId) {
      return this.selectedRowIds.some((id) =>
        this.ledgerIdsEqual(id, transactionId),
      );
    },

    formatNumber(value) {
      if (typeof value === 'string') {
        value = value.replace(/,/g, ''); // Remove commas if present
      }
      if (typeof value !== 'number') value = parseFloat(value);
      if (isNaN(value)) return '';
      if (value === 0.0) return '';
      const sign = value < 0 ? '- ' : '';
      return `${sign}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    handleDateRangeChange(dates) {
      this.ledgerFromDate = dates[0];
      this.ledgerToDate = dates[1];
      this.fetchTransactions();
    },

    showShortcuts() {
      // console.log('Requesting show shortcuts');
      eventBus.emit('show-shortcuts-app');
    },

    handleKeydown(event) {
      // Helper function to check if an input field has focus
      const isInputFocused = () => {
        const activeElement = document.activeElement;
        return (
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'SELECT' ||
            activeElement.contentEditable === 'true' ||
            activeElement.isContentEditable)
        );
      };

      // Payee address dialog (ManageAddress) open — never steal keys
      if (this.showAddressModal) {
        return;
      }

      if (event.key === '?' && this.hovered) {
        // Don't trigger showShortcuts if an input field has focus
        if (!isInputFocused()) {
          event.preventDefault();
          this.showShortcuts();
        }
      }
      if (event.key === 's' && this.hovered) {
        if (!isInputFocused()) {
          this.showSplits();
        }
      }
      if (event.key === 'n' && this.hovered) {
        // Don't trigger addNewEntry if an input field has focus
        if (!isInputFocused()) {
          this.addNewEntry();
        }
      }
      if (event.key === 'u' && this.hovered) {
        // Don't trigger findUnmatched if an input field has focus
        if (!isInputFocused()) {
          this.findUnmatched();
        }
      }
      if (event.key === 't' && this.hovered) {
        if (!isInputFocused()) {
          this.toggleTagSelected();
        }
      }
      if (event.key === 'e' && this.hovered) {
        if (!isInputFocused()) {
          if (this.selectedRow) {
            this.newLedgerRow = {};
            this.showEditDialog = true;
          } else {
            this.displayFooterMessage('Please select a row to edit');
          }
        }
      }
      if (event.key === 'p' && this.hovered) {
        if (!isInputFocused()) {
          if (this.selectedRow) {
            this.printCheck('preview');
          } else {
            this.displayFooterMessage('Please select a row to print check');
          }
        }
      }
      if (event.key === 'Escape' && this.hovered) {
        // Check if search input already has focus
        if (document.activeElement === this.$refs.searchQuery) {
          // If the search input already has focus and is empty, blur the input and clear selected rows
          if (this.rawSearchQuery === '') {
            this.$refs.searchQuery.blur();
            // Clear selected rows if search is already empty
            if (this.selectedRowIds.length > 0) {
              this.clearSelection();
            }
            return;
          }
          // If the search input already has focus but has content, clear the search
          this.searchQuery = '';
          this.rawSearchQuery = '';
          return;
        }

        // If search input is not empty, focus it and select all text
        if (this.rawSearchQuery !== '') {
          if (this.$refs.searchQuery) {
            this.$refs.searchQuery.focus();
            this.$refs.searchQuery.select();
          }
          return;
        }

        // If search input is empty and no input has focus, clear selected rows
        if (this.selectedRowIds.length > 0) {
          this.clearSelection();
          return;
        }

        // Otherwise, focus on search input
        if (this.$refs.searchQuery) {
          this.$refs.searchQuery.focus();
          this.$refs.searchQuery.select();
        }
        return;
      }
      if (event.key === 'Enter' && this.selectedRow && this.hovered) {
        this.showEditDialog = true;
      } else if (event.key === 'V' && this.selectedRow && this.hovered) {
        if (!isInputFocused()) {
          this.voidTransaction(this.selectedRow);
        }
      } else if (
        event.key === 'L' &&
        this.hovered &&
        this.selectedRowIds.length === 1 &&
        !isInputFocused()
      ) {
        // Shift+L: link open document viewer doc to the selected ledger row
        event.preventDefault();
        eventBus.emit('linkDocumentToSelectedLedger');
      } else if (event.key === 'Delete' && this.selectedRow && this.hovered) {
        if (!isInputFocused()) {
          event.preventDefault();
          this.deleteCheckPos = {
            x: Math.min(this.mousePos.x + 10, window.innerWidth - 220),
            y: Math.max(this.mousePos.y - 100, 10),
          };
          this.showDeleteCheckConfirm = true;
        }
      } else if (this.hovered && event.key == 'f' && this.selectedRow) {
        // If we're in an input field, allow normal typing behavior
        if (isInputFocused()) {
          return;
        }

        // Otherwise prevent default and handle the 'f' key functionality
        event.preventDefault();

        // set the search query in MainPage.vue to this.selectedRow.CheckNum
        if (this.selectedRow.CheckNum) {
          // If search input is empty, send CheckNum to MainPage
          if (!this.rawSearchQuery) {
            eventBus.emit('set-search-query', this.selectedRow.CheckNum);
            this.displayFooterMessage(
              `Search for check number: ${this.selectedRow.CheckNum}`,
            );
          } else {
            // Otherwise, set the search query in MainPage.vue to this.rawSearchQuery
            eventBus.emit('set-search-query', this.rawSearchQuery);
            this.displayFooterMessage(`Search for: ${this.rawSearchQuery}`);
          }
        } else {
          this.displayFooterMessage(
            'Selected row does not have a check number',
          );
          // console.log( 'Selected row does not have a check number', this.selectedRow,);
        }
      } else if (event.ctrlKey && this.hovered && event.key === 'ArrowDown') {
        this.scrollToBottom();
      } else if (this.hovered && event.ctrlKey && event.key === 'ArrowUp') {
        this.scrollToTop();
      } else if (
        this.hovered &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        // Only handle arrow keys if not in an input field
        const activeElement = document.activeElement;
        const isInputField =
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable;

        if (isInputField) {
          return;
        }

        event.preventDefault();

        // Find current account index
        const currentIndex = this.accountOptions.findIndex(
          (account) => account.accountNumber === this.selectedAccount,
        );

        if (currentIndex !== -1) {
          let newIndex;
          if (event.key === 'ArrowRight') {
            // Move to next account (or wrap to first)
            newIndex = (currentIndex + 1) % this.accountOptions.length;
          } else {
            // Move to previous account (or wrap to last)
            newIndex =
              currentIndex === 0
                ? this.accountOptions.length - 1
                : currentIndex - 1;
          }

          // Update selected account
          this.selectedAccount = this.accountOptions[newIndex].accountNumber;
          this.handleAccountChange();

          this.displayFooterMessage(
            `Switched to account: ${this.accountOptions[newIndex].accountNumber} : ${this.accountOptions[newIndex].accountName}`,
          );

          // If no row is currently selected, select the top row after account change
          this.$nextTick(() => {
            if (
              this.selectedRowIds.length === 0 &&
              this.filteredTransactions.length > 0
            ) {
              const topTransaction = this.filteredTransactions[0];
              this.selectedRow = topTransaction;
              this.selectedRowIds = [topTransaction.Id];
              this.$emit('rowSelected', topTransaction); // Same as single-click
              this.$emit('selectedLedgerRow', this.selectedRow);
              this.$emit('selectedLedgerRows', [topTransaction]);
              this.notifyUserLedgerSelection(this.selectedRow);

              // Scroll to the selected row
              this.$nextTick(() => {
                const selectedRowElement = document.querySelector(
                  `#row-${topTransaction.Id}`,
                );
                if (selectedRowElement) {
                  selectedRowElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              });
            }
          });
        }
      } else if (
        this.hovered &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
        !event.ctrlKey
      ) {
        // Only handle arrow keys if not in an input field and a row is selected
        const activeElement = document.activeElement;
        const isInputField =
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable;

        if (!(isInputField || this.selectedRowIds.length !== 1)) {
          event.preventDefault();

          const currentId = this.selectedRowIds[0];
          const currentIndex = this.filteredTransactions.findIndex(
            (transaction) => transaction.Id === currentId,
          );

          if (currentIndex !== -1) {
            let newIndex;
            if (event.key === 'ArrowDown') {
              // Move to next row (with bounds checking)
              newIndex = Math.min(
                currentIndex + 1,
                this.filteredTransactions.length - 1,
              );
            } else {
              // Move to previous row (with bounds checking)
              newIndex = Math.max(currentIndex - 1, 0);
            }

            // Only update if we're moving to a different row
            if (newIndex !== currentIndex) {
              const newTransaction = this.filteredTransactions[newIndex];
              this.selectedRow = newTransaction;
              this.selectedRowIds = [newTransaction.Id];
              this.$emit('rowSelected', newTransaction); // Same as single-click
              this.$emit('selectedLedgerRow', this.selectedRow);
              this.$emit('selectedLedgerRows', [newTransaction]);
              this.notifyUserLedgerSelection(this.selectedRow);

              // Scroll the new row into view
              this.$nextTick(() => {
                const selectedRowElement = document.querySelector(
                  `#row-${newTransaction.Id}`,
                );
                if (selectedRowElement) {
                  selectedRowElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              });
            }
          }
        }
      } else if (
        event.key === 'm' &&
        this.hovered &&
        this.selectedRowIds.length === 1
      ) {
        const transaction = this.transactions.find(
          (t) => t.Id === this.selectedRowIds[0],
        );
        if (transaction) {
          // Make sure to emit selectedLedgerRows before requesting match
          const selectedRows = [transaction];
          this.$emit('selectedLedgerRows', selectedRows);

          // Emit requestHandleMatch event which will be handled by MainPage.vue
          this.$emit('requestHandleMatch', transaction);

          // The actual update of transaction.Matched will be handled by MainPage.vue
          // We'll listen for a match success event to update our UI filter if needed
        }
      }
    },

    scrollToBottom() {
      const tableContainer = this.$refs.tableContainer;
      if (tableContainer) {
        tableContainer.scrollTop = tableContainer.scrollHeight;
      }
    },

    scrollToTop() {
      const tableContainer = this.$refs.tableContainer;
      if (tableContainer) {
        tableContainer.scrollTop = 0;
      }
    },

    async voidTransaction(selectedRow) {
      const token = localStorage.getItem('token');
      try {
        await axios.post(
          '/api/voidTransaction/',
          {
            Id: selectedRow.Id,
            // toggle Voided
            Voided: 1 - selectedRow.Voided,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        this.selectedRow.Voided = 1 - selectedRow.Voided;
      } catch (error) {
        console.error('Error voiding transaction. Details:', error);
      }
    },

    async fetchSplitDetails(id) {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`/api/qbtransactions/details/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.splitDetails = response.data;
      } catch (error) {
        console.error('Error fetching split details:', error);
      }
    },

    closeSplitDetails() {
      this.showSplitDetails = false;
      this.highlightedRowId = null;
      this.$emit('update-show-split-details', false); // Emit event
    },

    // For edit dialog
    handleMouseEnter() {
      this.hovered = true;
      this.displayFooterMessage('ledger active');
    },

    handleMouseLeave() {
      this.hovered = false;
      this.displayFooterMessage('ledger inactive');
      // clear focus from the search input
      if (this.$refs.searchQuery) {
        this.$refs.searchQuery.blur();
      }
    },

    addNewEntry() {
      this.showEditDialog = true;
      this.selectedRow = null;
      this.newLedgerRow = {};
      this.isNew = true;
      this.selectedRow = null;
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.isNew = false;
    },

    async confirmDeleteCheck() {
      this.showDeleteCheckConfirm = false;
      if (!this.selectedRow) return;
      const deletedId = this.selectedRow.Id;
      try {
        await axios.post('/api/del-ledger-entry', {
          id: deletedId,
        });
        // Remove the entry and any split children from local list
        this.transactions = this.transactions.filter(
          (t) => t.Id !== deletedId && t.LinkedSplitId !== deletedId,
        );
        this.displayFooterMessage(`Deleted entry #${deletedId}`);
        this.selectedRow = null;
        this.selectedRowIds = [];
      } catch (error) {
        console.error('Error deleting check:', error);
        this.displayFooterMessage('Failed to delete check');
      }
    },

    async upsertTOransaction(updatedTransaction) {
      const token = localStorage.getItem('token');
      // Make an API call to update or insert the transaction
      try {
        await axios.post(
          '/api/qbtransactions/upsert',
          {
            transaction: updatedTransaction,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        // Refresh the table data
        this.fetchTransactions();
      } catch (error) {
        console.error('Error upserting transaction:', error);
      }
    },

    updateSearchQuery(value) {
      this.searchQuery = value;
    },

    print(elementName) {
      const table = document.getElementById(elementName);
      let printContent = table.outerHTML;

      // Check if we need to suppress columns to the right of 'Balance'
      if (this.suppressRightColumns) {
        // Clone the table element to manipulate it without affecting the DOM
        const tableClone = table.cloneNode(true);

        // Remove columns to the right of 'Balance' from the clone
        tableClone.querySelectorAll('tr').forEach((row) => {
          row
            .querySelectorAll('td:nth-child(n+11), th:nth-child(n+11)')
            .forEach((cell) => {
              cell.remove();
            });
        });

        // Update printContent with the modified table
        printContent = tableClone.outerHTML;
      }

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Table</title>
            <style>
              @page {
              size: landscape;
            }
              /* General table styles for printing */
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.6em;
                font-family: consolas;
              }
              table, th, td {
                border: 1px solid #888;
              }
              th, td {
                padding: 4px;
                text-align: left;
              }
              .even {
                background-color: #dddbdb; /* Light gray for even rows */
              }
              .odd {
                background-color: #white; /* Light gray for even rows */
              }
              .right-align {
                text-align: right;
                min-width:65px
              }
              /* Force browsers to print background colors */
              @media print {
                body {
                  -webkit-print-color-adjust: exact; /* Chrome, Safari */
                  print-color-adjust: exact; /* Firefox */
                }
              }
            </style>
          </head>
         <body onload="window.print(); window.close();">
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    // Export current filtered ledger rows to Excel (same flow as bank export)
    async exportTransactions() {
      try {
        if (
          !this.filteredTransactions ||
          this.filteredTransactions.length === 0
        ) {
          alert('No transactions to export. Please adjust your filters.');
          return;
        }

        const transactions = this.filteredTransactions.map((transaction) => ({
          ...transaction,
          Tagged: this.taggedIds.some((tid) =>
            this.ledgerIdsEqual(tid, transaction.Id),
          )
            ? 1
            : 0,
        }));

        const response = await axios.post(
          '/api/export-ledger-transactions',
          { transactions },
          { responseType: 'blob' },
        );

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'ledger.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2)
            filename = filenameMatch[1];
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting ledger transactions:', error);
        alert('Failed to export transactions. Please try again.');
      }
    },

    printerFriendly() {
      this.isPrinterFriendly = !this.isPrinterFriendly;
      this.noWrapText = !this.noWrapText;
      this.suppressRightColumns = !this.suppressRightColumns;
    },

    async findUnmatched() {
      this.findUnmatchedMode = !this.findUnmatchedMode;

      if (this.findUnmatchedMode) {
        // Entering unmatched mode
        this.searchQuery = '';
        this.rawSearchQuery = '';
        this.showSplitsInTable = false;
        await this.resetDateRange();
        this.$nextTick(() => {
          this.selectAllFilteredRows();
        });
      } else {
        // Exiting unmatched mode
        this.clearSelection();
        this.searchQuery = '';
        this.rawSearchQuery = '';
        await this.resetDateRange();
      }
    },

    // Set search to verify mode
    setVerifySearch() {
      this.searchQuery = ':verify';
      this.rawSearchQuery = ':verify';
      // Enable show splits to see parent and children rows
      this.showSplitsInTable = true;
      this.noDoubleEntry = false;
      // Also set the date range to current year like the resetDateRange button
      this.resetDateRangeToYTD();
    },

    selectAllFilteredRows() {
      this.selectedRowIds = this.filteredTransactions.map((t) => t.Id);
      const selectedRows = this.filteredTransactions.slice(); // Copy array
      this.$emit('selectedLedgerRows', selectedRows);
    },

    toggleTagSelected() {
      if (!this.selectedRowIds?.length) {
        this.displayFooterMessage('Select rows first, then press t to tag');
        return;
      }

      // Normalize to numbers so multi-select (mixed string/number Ids) still toggles
      const taggedSet = new Set(
        this.taggedIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n) && n),
      );
      let taggedCount = 0;
      let untaggedCount = 0;

      this.selectedRowIds.forEach((id) => {
        const n = Number(id);
        if (!n || Number.isNaN(n)) return;
        if (taggedSet.has(n)) {
          taggedSet.delete(n);
          untaggedCount += 1;
        } else {
          taggedSet.add(n);
          taggedCount += 1;
        }
      });

      // Reassign so Vue re-renders the Tagged column for every selected row
      this.taggedIds = Array.from(taggedSet);
      localStorage.setItem('qb_taggedIds', JSON.stringify(this.taggedIds));
      this.displayFooterMessage(
        `Tag toggle: +${taggedCount} / −${untaggedCount} (${this.selectedRowIds.length} selected)`,
      );
    },

    async filterUtilities() {
      this.filterUtilitiesMode = !this.filterUtilitiesMode;

      if (this.filterUtilitiesMode) {
        // Entering utilities filter mode - turn off other filters
        this.findUnmatchedMode = false;
        this.filterTaggedMode = false;
        this.searchQuery = '';
        this.rawSearchQuery = '';

        // Enable splits so child transactions with Utilities account are visible
        if (!this.showSplitsInTable) {
          this.showSplitsInTable = true;
          this.noDoubleEntry = false;
          await this.fetchTransactions();
        }

        this.$nextTick(() => {
          this.selectAllFilteredRows();
        });
        this.displayFooterMessage('Filtering for Utilities transactions');
      } else {
        // Exiting utilities filter mode
        this.showSplitsInTable = false;
        this.clearSelection();
        await this.fetchTransactions();
      }
    },

    filterTagged() {
      this.filterTaggedMode = !this.filterTaggedMode;

      if (this.filterTaggedMode) {
        // Entering tagged filter mode - turn off unmatched filter
        this.findUnmatchedMode = false;
        this.filterUtilitiesMode = false;
        if (this.taggedIds.length === 0) {
          this.displayFooterMessage('No tagged rows to filter');
          this.filterTaggedMode = false;
          return;
        }
        this.displayFooterMessage(
          'Filtering unmatched tagged rows in the current date range',
        );
        this.$nextTick(() => {
          this.selectAllFilteredRows();
        });
      } else {
        // Exiting tagged filter mode
        this.clearSelection();
      }
    },

    debouncedUpdateSearchQuery: debounce(function (newVal) {
      this.searchQuery = newVal;
    }, 300),

    /**
     * If this check still says PRINT, replace it with max(Num)+1 and save
     * the ledger row before any preview is opened.
     */
    async assignPrintedCheckNumber(ledgerData) {
      const targets = printPlaceholderEntries(ledgerData);
      if (!targets.length) return true;

      let maxNum;
      try {
        const response = await axios.get('/api/get-max-num');
        maxNum = response.data;
      } catch (error) {
        console.error('Error fetching next check number:', error);
        this.displayFooterMessage('Could not get the next check number');
        return false;
      }

      const next = nextCheckNumber(maxNum);
      if (next == null) {
        this.displayFooterMessage('Could not get the next check number');
        return false;
      }

      try {
        for (const entry of targets) {
          const payload = ledgerCheckNumberUpdate(entry, next);
          if (!payload) {
            this.displayFooterMessage(
              'Check date is invalid, so PRINT was not replaced',
            );
            return false;
          }
          await axios.post('/api/update-ledger-entry', payload);
        }
      } catch (error) {
        console.error('Error saving next check number:', error);
        this.displayFooterMessage('Failed to save the next check number');
        return false;
      }

      const ids = applyAssignedCheckNumber(ledgerData, next);
      const nextText = String(next);
      const idSet = new Set(ids.map((rowId) => Number(rowId)));
      for (const row of this.transactions) {
        if (idSet.has(Number(row.Id))) row.CheckNum = nextText;
      }
      if (this.selectedRow && idSet.has(Number(this.selectedRow.Id))) {
        this.selectedRow = { ...this.selectedRow, CheckNum: nextText };
      }
      this.displayFooterMessage(`Check number set to ${nextText}`);
      return true;
    },

    async printCheck(preview, overrideId = null) {
      const id = overrideId || this.selectedRowIds[0];
      if (!id) {
        this.displayFooterMessage(
          'Please select exactly one row to print the check.',
        );
        return false;
      }
      this.displayFooterMessage(`Generating check for ID: ${id}`);
      const generated = await this.generateCheck(id, preview);
      if (generated === false) return false;
      console.log(
        '📄 [QBPAGE] Emitting closeNavBarPanels - opening PDF viewer',
      );
      eventBus.emit('closeNavBarPanels');
      this.showPDF = true;
    },

    // create convertWholeNumberToWords function. Given a number like 123, it should return "One hundred twenty-three"
    convertWholeNumberToWords(number) {
      const ones = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen',
      ];
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety',
      ];

      let words = '';

      if (number >= 1000) {
        words +=
          this.convertWholeNumberToWords(Math.floor(number / 1000)) +
          ' Thousand ';
        number %= 1000;
      }

      if (number >= 100) {
        words += ones[Math.floor(number / 100)] + ' Hundred ';
        number %= 100;
      }

      if (number >= 20) {
        words += tens[Math.floor(number / 10)] + ' ';
        number %= 10;
      }

      if (number > 0) {
        words += ones[number] + ' ';
      }

      return words.trim();
    },

    // create convertDecimalToWords function. Given a number like 45, it should return "45/100"
    convertDecimalToWords(decimal) {
      if (!decimal) {
        return '00/100';
      }

      // Ensure the decimal part is padded to two digits
      const paddedDecimal = decimal.padEnd(2, '0');
      return `${paddedDecimal}/100`;
    },

    // create convertToWords function. Given a number like 123.45, it should return "One hundred twenty-three and 45/100"
    convertToWords(number) {
      // Ensure the number is a string before splitting
      const [whole, decimal] = number.toString().split('.');

      // Convert the whole number to words
      const wholeWords = this.convertWholeNumberToWords(whole);

      // Convert the decimal number to words
      const decimalWords = this.convertDecimalToWords(decimal);

      // Combine the whole and decimal parts
      return `${wholeWords} and ${decimalWords}`;
    },

    // check generation
    async generateCheck(id, showPreview) {
      console.log('Generating check for ID:', id);
      // Remove any existing temp-print-div from previous calls
      const existingTempDiv = document.getElementById('temp-print-div');
      if (existingTempDiv) {
        document.body.removeChild(existingTempDiv);
      }

      // if id is null, this.displayFooterMessage('Please select a row to print the check.');
      if (!id) {
        this.displayFooterMessage('Please select a row to print the check.');
        return;
      }
      try {
        // Fetch ledger entry data
        const getLedgerResponse = await axios.get(
          `/api/get-ledger-entry/${id}`,
        );
        const ledgerData = getLedgerResponse.data;
        const parentEntry = ledgerData.parentEntry;
        const siblings = ledgerData.siblings;

        // PRINT is a placeholder. Assign the next number and save it
        // before the preview is built, the same way >> does in Ledger Entry.
        const numbered = await this.assignPrintedCheckNumber(ledgerData);
        if (!numbered) return false;

        // Fetch address data
        let addressData = null;
        try {
          console.log('get-address');
          const getAddressResponse = await axios.get('/api/get-address', {
            params: {
              name: encodeURIComponent(parentEntry.Name),
            },
          });
          // Use the direct response data as the address object
          addressData = getAddressResponse.data;
        } catch (error) {
          console.error('Error fetching address:', error);
        }

        // Prepare voucher details
        const maxVoucherDetails = 12;
        let voucherDetails =
          siblings?.length > 0
            ? siblings.slice(0, maxVoucherDetails).map((sibling) => ({
                description: sibling.Memo,
                amount: sibling.Amount,
              }))
            : {};

        if (siblings?.length > maxVoucherDetails) {
          const remainingAmount = siblings
            .slice(maxVoucherDetails)
            .reduce((sum, sibling) => sum + parseFloat(sibling.Amount), 0);
          voucherDetails.push({
            description: 'Too many items. additional split items total:',
            amount: remainingAmount.toFixed(2),
          });
        }
        // Prepare request data
        const requestData = {
          preview: showPreview === 'preview', // Set preview to true if showPreview is 'preview', false otherwise
          checkNum:
            parentEntry.Type.toLowerCase() === 'check'
              ? parentEntry.Num
              : 'debit',
          payee: parentEntry.Name,
          date: new Date(parentEntry.Date).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
          }),
          amount: Math.abs(parseFloat(parentEntry.Amount)).toFixed(2), // Ensure two digits to the right of the decimal
          amountWords: this.convertToWords(Math.abs(parentEntry.Amount)),
          address: addressData,
          memo: parentEntry.Memo,
          account:
            parentEntry.IsSplitParent === 0
              ? parentEntry.Split
              : parentEntry.Account,
          voucherDetails: voucherDetails,
        };

        // Send POST request to the backend API
        const response = await axios.post('/api/generate-check', requestData, {
          responseType: 'arraybuffer', // Using 'arraybuffer' to handle binary data
        });

        if (response.status === 200) {
          // Create a Blob from the response data
          const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          if (showPreview === 'preview') {
            // Show the PDF in the viewer
            const pdfUrl = URL.createObjectURL(pdfBlob);
            this.pdfUrl = pdfUrl;
            console.log(
              '📄 [QBPAGE] Emitting closeNavBarPanels - opening PDF viewer',
            );
            eventBus.emit('closeNavBarPanels');
            this.showPDF = true;
          } else {
            // Create a temporary hidden div, set its content to the PDF, and trigger print
            const tempDiv = document.createElement('div');
            tempDiv.id = 'temp-print-div';
            tempDiv.style.display = 'none'; // Hide the div

            // Convert Blob to URL for embedding
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Create an iframe with specific styles for 8.5" x 11" (612px x 792px at 72 DPI)
            tempDiv.innerHTML = `
    <style>
      @media print {
      @page {
        size: 8.5in 11in; /* Set page size to 8.5" x 11" */
        margin: 0; /* Remove all margins */
        padding: 0; /* Remove all padding */
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
      }
      iframe {
        width: 612px !important; /* 8.5" at 72 DPI */
        height: 792px !important; /* 11" at 72 DPI */
        border: none;
        margin: 0 !important;
        padding: 0 !important;
      }
      }
      /* Default styling for screen (hidden div) */
      iframe {
      width: 612px; /* 8.5" at 72 DPI */
      height: 792px; /* 11" at 72 DPI */
      border: none;
      margin: 0;
      padding: 0;
      }
    </style>
    <iframe id="pdf-iframe" src="${pdfUrl}"></iframe>
    `;
            document.body.appendChild(tempDiv);

            // Wait for the iframe to load before triggering the print dialog
            const iframe = document.getElementById('pdf-iframe');
            iframe.onload = () => {
              const printWindow = iframe.contentWindow;
              printWindow.focus();
              printWindow.print();
              printWindow.onafterprint = () => {
                document.body.removeChild(tempDiv);
                URL.revokeObjectURL(pdfUrl); // Clean up the object URL
              };
            };
          }
        } else {
          console.error(
            'Failed to generate PDF check. Status:',
            response.status,
          );
        }
      } catch (error) {
        console.error('Error generating check:', error);
      }
    },

    closeAddressModal() {
      this.showAddressModal = false;
      this.printCheck('preview');
      if (this.selectedRowIds.length > 0) {
        this.generateCheck(this.selectedRowIds[0]);
      }
    },

    handlePrintCheck(ledgerEntryId) {
      this.printCheck('preview', ledgerEntryId);
    },

    navigateCheck(direction) {
      const currentId = this.selectedRowIds[0];
      if (!currentId) return;
      const currentNum = Number(currentId);
      const checks = this.filteredTransactions.filter((t) =>
        t.Type?.toLowerCase().includes('check'),
      );
      const candidates =
        direction > 0
          ? checks.filter((t) => Number(t.Id) > currentNum)
          : checks.filter((t) => Number(t.Id) < currentNum);
      if (!candidates.length) {
        this.displayFooterMessage(
          direction < 0 ? 'No previous check found' : 'No next check found',
        );
        return;
      }
      candidates.sort((a, b) => Number(a.Id) - Number(b.Id));
      const transaction =
        direction > 0 ? candidates[0] : candidates[candidates.length - 1];
      this.selectedRow = transaction;
      this.selectedRowIds = [transaction.Id];
      this.$emit('rowSelected', transaction);
      this.$emit('selectedLedgerRow', transaction);
      this.notifyUserLedgerSelection(transaction);
      this.printCheck('preview');
      this.$nextTick(() => {
        const el = document.querySelector(`#row-${transaction.Id}`);
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },

    handleNewLedgerRow(data) {
      this.showEditDialog = true;
      this.selectedRow = null;
      this.newLedgerRow = data;
      this.isNew = true;
    },

    /** Convert mm/dd/yy (or ISO date) to MySQL datetime for ledger APIs. */
    toMySQLDateTime(dateStr) {
      if (!dateStr) return null;
      const s = String(dateStr).trim();
      const mdY = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (mdY) {
        let year = mdY[3];
        if (year.length === 2) year = `20${year}`;
        return `${year}-${mdY[1].padStart(2, '0')}-${mdY[2].padStart(2, '0')} 00:00:00`;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        return s.includes(' ') ? s : `${s.slice(0, 10)} 00:00:00`;
      }
      return null;
    },

    /**
     * From MainPage Shift+I: create split children for selected bank debits
     * under the selected ledger parent, then open the edit dialog.
     */
    async handleCreateSplitChildrenFromBankRows({ parent, bankTransactions }) {
      if (!parent?.Id || !Array.isArray(bankTransactions) || !bankTransactions.length) {
        this.displayFooterMessage('Missing parent or bank rows for split create');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        let getResp = await axios.get(`/api/get-ledger-entry/${parent.Id}`, {
          headers,
        });
        let parentEntry = getResp.data?.parentEntry;
        if (!parentEntry) {
          this.displayFooterMessage(`Ledger parent #${parent.Id} not found`);
          return;
        }

        // If user selected a split child, attach to the real parent instead
        const parentId = parentEntry.IsSplitParent
          ? parentEntry.Id
          : parentEntry.LinkedSplitId || parentEntry.Id;
        if (parentId !== parentEntry.Id) {
          getResp = await axios.get(`/api/get-ledger-entry/${parentId}`, {
            headers,
          });
          parentEntry = getResp.data?.parentEntry;
          if (!parentEntry) {
            this.displayFooterMessage(`Ledger parent #${parentId} not found`);
            return;
          }
        }

        const existingSiblings = Array.isArray(getResp.data.siblings)
          ? getResp.data.siblings
          : [];
        const existingSum = existingSiblings.reduce(
          (sum, s) => sum + (parseFloat(s.Amount) || 0),
          0,
        );

        // Expense/category account from the selected row (children keep this).
        // Split parents must use the bank account so they stay visible under
        // the selected bank filter (Account or Split includes bank name).
        const childAccount = parentEntry.Account || parent.Account || '';
        const bankAccount =
          this.selectedAccount != null && this.selectedAccountName
            ? `${this.selectedAccount} : ${this.selectedAccountName}`
            : parentEntry.Split &&
                !String(parentEntry.Split).includes('-SPLIT-')
              ? parentEntry.Split
              : childAccount;

        const mysqlDate =
          this.toMySQLDateTime(parentEntry.Date) ||
          this.toMySQLDateTime(parent.Date);
        if (!mysqlDate) {
          this.displayFooterMessage('Parent ledger date is invalid');
          return;
        }

        // First time converting a non-split row: keep its original amount as a child
        // so it is not lost when the parent becomes the -SPLIT- shell.
        const wasAlreadySplitParent = !!parentEntry.IsSplitParent;
        const originalAmount = parseFloat(parentEntry.Amount) || 0;
        let preservedSum = 0;
        if (!wasAlreadySplitParent && originalAmount !== 0) {
          preservedSum = originalAmount;
          await axios.put(
            '/api/add-ledger-entry',
            {
              Date: mysqlDate,
              Type: parentEntry.Type || parent.Type || '',
              Num: parentEntry.Num || parent.CheckNum || parent.Num || '',
              Amount: originalAmount,
              Name: '',
              Memo: parentEntry.Memo || '',
              Account: childAccount,
              Split: bankAccount,
              Class: parentEntry.Class || '',
              IsSplitParent: false,
              LinkedSplitId: parentId,
            },
            { headers },
          );
        }

        let newSum = 0;
        for (const bank of bankTransactions) {
          // Split children use positive amounts for debits; negative = credit.
          // Bank Debit is often signed negative — store absolute value.
          const amount = Math.abs(parseFloat(bank.Debit) || 0);
          newSum += amount;
          // Bank Description column is BankRefDesc (not bank.Memo)
          const memo = (
            bank.BankRefDesc ||
            bank.Description ||
            bank.Memo ||
            ''
          ).trim();

          await axios.put(
            '/api/add-ledger-entry',
            {
              Date: mysqlDate,
              Type: parentEntry.Type || parent.Type || '',
              Num: parentEntry.Num || parent.CheckNum || parent.Num || '',
              Amount: amount,
              Name: '',
              Memo: memo,
              Account: childAccount,
              Split: bankAccount,
              Class: 'Big Property Management',
              IsSplitParent: false,
              LinkedSplitId: parentId,
            },
            { headers },
          );
        }

        const totalSiblingAmount = existingSum + preservedSum + newSum;
        const parentAmount = -parseFloat(totalSiblingAmount.toFixed(2));

        await axios.post(
          '/api/update-ledger-entry',
          {
            Id: parentId,
            Date: mysqlDate,
            Type: parentEntry.Type || '',
            Num: parentEntry.Num || '',
            Amount: parentAmount,
            Name: parentEntry.Name || '',
            Memo: parentEntry.Memo || '',
            Account: bankAccount,
            Split: '-SPLIT-',
            Class: parentEntry.Class || '',
            IsSplitParent: true,
            LinkedSplitId: null,
          },
          { headers },
        );

        // Ensure split children are visible in the ledger table
        this.showSplitsInTable = true;
        this.noDoubleEntry = false;

        // Remount dialog so it reloads parent + new siblings
        this.showEditDialog = false;
        this.isNew = false;
        this.newLedgerRow = null;
        await this.$nextTick();

        await this.fetchTransactions();
        const transaction = this.findTransactionById(parentId);
        this.selectedRow = transaction || {
          Id: parentId,
          ...parentEntry,
          Split: '-SPLIT-',
          IsSplitParent: 1,
          Amount: parentAmount,
        };
        this.selectedRowIds = [parentId];
        this.showEditDialog = true;

        this.displayFooterMessage(
          `Created ${bankTransactions.length} split child(ren) under ledger #${parentId}`,
        );
      } catch (error) {
        console.error('Error creating split children from bank rows:', error);
        this.displayFooterMessage(
          error.response?.data?.error ||
            'Failed to create split children from bank rows',
        );
      }
    },

    handleBankRowSelected(data) {
      this.selectedBankRow = data;
    },

    handleMatchSuccess(transactionId) {
      // Find the transaction in our local collection
      const transaction = this.transactions.find((t) => t.Id === transactionId);
      if (transaction) {
        // Update the matched status
        transaction.Matched = 1;

        // Force a UI update by creating a new array reference
        this.transactions = [...this.transactions];

        this.displayFooterMessage('Transaction successfully matched');

        // If search query is filtering for unmatched transactions, update it to show matched ones
        if (this.searchQuery && this.searchQuery.includes(':!m')) {
          this.searchQuery = this.searchQuery.replace(':!m', ':m');
        }
      }
    },

    setSearchInput(value) {
      this.searchQuery = value;
      this.rawSearchQuery = value;
      this.showSplits();
    },

    async handleLedgerEntrySaved(savedId) {
      const id = Number(savedId);
      console.log('handleLedgerEntrySaved called with savedId:', savedId, id);
      if (!id) return;

      // Socket/fetch rebinds will keep this id selected until we finish
      this.pendingSelectLedgerId = id;
      this.selectedRowIds = [id];

      try {
        const response = await axios.get(`/api/get-ledger-entry/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.data && response.data.parentEntry) {
          const parent = response.data.parentEntry;
          const selectedSibling = response.data.selectedSibling;
          console.log('Fetched saved transaction:', parent);

          // Must await — otherwise fetch uses the old date range and misses the row
          await this.extendDateRangeIfNeeded(parent);

          // Prefer the row that was actually saved (sibling vs parent) for Class
          let savedEntry = parent;
          if (
            selectedSibling &&
            Number(selectedSibling.Id) === id
          ) {
            savedEntry = selectedSibling;
          } else if (
            Number(parent.Id) !== id &&
            Array.isArray(response.data.siblings)
          ) {
            const sib = response.data.siblings.find(
              (s) => Number(s.Id) === id,
            );
            if (sib) savedEntry = sib;
          }

          // Bank link is usually on the matched parent / non-split row
          const bankTransactionId =
            parent.LinkedTransactionId ||
            parent.linkedTransactionId ||
            savedEntry.LinkedTransactionId ||
            savedEntry.linkedTransactionId ||
            null;

          // Prefer Class from the row that was just saved so bank Class tracks ledger edits
          const classForBank =
            savedEntry.Class != null ? savedEntry.Class : parent.Class;

          await this.updateLinkedBankTransaction({
            Id: parent.Id || savedEntry.Id,
            Date: parent.Date || savedEntry.Date,
            Num: parent.Num || savedEntry.Num || '',
            Type: parent.Type || savedEntry.Type || '',
            Name: parent.Name || savedEntry.Name || '',
            Account: parent.Account || savedEntry.Account || '',
            Class: classForBank || '',
            Memo: parent.Memo || savedEntry.Memo || '',
            IsSplitParent: parent.IsSplitParent || 0,
            LinkedTransactionId: bankTransactionId,
            Matched: parent.Matched || savedEntry.Matched || 0,
          });
        }
      } catch (error) {
        console.error(
          'Error fetching saved transaction for date range check:',
          error,
        );
      }

      // Find with loose id match (socket/API may use string vs number)
      let transaction = this.findTransactionById(id);
      if (!transaction) {
        await this.fetchTransactions();
        transaction = this.findTransactionById(id);
      }

      if (transaction) {
        await this.updateSelectedTransaction(transaction, id);
      } else {
        this.displayFooterMessage(
          `Saved ledger #${id}, but it is not in the current list (check date range / filters)`,
        );
      }

      this.pendingSelectLedgerId = null;
      // If anything overwrote the document viewer during save, restore it
      eventBus.emit('preserveViewerDocument');
    },

    async updateLinkedBankTransaction(ledgerEntry) {
      const bankTransactionId =
        ledgerEntry.LinkedTransactionId ||
        ledgerEntry.linkedTransactionId ||
        null;

      // Only proceed if the ledger entry has a linked bank transaction
      if (!bankTransactionId) {
        console.log(
          'No linked bank transaction found for ledger entry:',
          ledgerEntry.Id,
        );
        return;
      }

      // Date is required for the bank datetime field; Name may be empty
      if (!ledgerEntry.Date) {
        console.log(
          'Skipping bank transaction update - missing ledger Date',
        );
        return;
      }

      console.log('Updating linked bank transaction:', {
        ledgerEntryId: ledgerEntry.Id,
        bankTransactionId,
        ledgerName: ledgerEntry.Name,
        ledgerAccount: ledgerEntry.Account,
        ledgerClass: ledgerEntry.Class,
      });

      try {
        // Prepare the fields to update in the bank transaction (same as in handleMatch)
        // Class is always included so Class edits on matched ledger rows flow to bank
        const updatedFields = {
          datetime: new Date(ledgerEntry.Date)
            .toISOString()
            .slice(0, 19)
            .replace('T', ' '),
          checkNumber: ledgerEntry.Num || '',
          Type: ledgerEntry.Type || '',
          Description: ledgerEntry.Name || '',
          Account: ledgerEntry.Account || '',
          Class: ledgerEntry.Class || '',
          Memo: ledgerEntry.Memo || '',
          IsSplitParent: ledgerEntry.IsSplitParent || 0,
        };

        console.log('Sending update fields:', updatedFields);

        const token = localStorage.getItem('token');
        const response = await axios.post(
          '/api/update-bank-transaction-fields',
          {
            bankTransactionId,
            updatedFields,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          console.log('Bank transaction updated successfully:', {
            bankTransactionId,
            modifiedBy: response.data.modifiedBy,
            affectedRows: response.data.affectedRows,
          });
          // Emit event to refresh bank transactions in MainPage
          eventBus.emit('bankTransactionUpdated', bankTransactionId);
        } else {
          console.warn(
            'Bank transaction update returned success: false',
            response.data,
          );
        }
      } catch (error) {
        console.error('Error updating linked bank transaction:', {
          bankTransactionId,
          error: error.message,
          response: error.response?.data,
        });
      }
    },

    async extendDateRangeIfNeeded(transaction) {
      // Only proceed if transaction and transaction.Date exist
      if (!transaction || !transaction.Date) return;

      console.log(
        'extendDateRangeIfNeeded called with transaction.Date:',
        transaction.Date,
      );
      console.log('Current ledgerToDate:', this.ledgerToDate);

      // Parse the transaction date - handle MM/DD/YY format
      let entryDate;
      if (transaction.Date.includes('/')) {
        // Handle MM/DD/YY format
        const dateParts = transaction.Date.split('/');
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[0], 10) - 1; // Month is 0-based
          const day = parseInt(dateParts[1], 10);
          let year = parseInt(dateParts[2], 10);
          // Handle 2-digit years - assume 20xx for years 00-99
          if (year < 100) {
            year += 2000;
          }
          entryDate = new Date(year, month, day);
        } else {
          entryDate = new Date(transaction.Date);
        }
      } else {
        entryDate = new Date(transaction.Date);
      }

      const currentEndDate = new Date(this.ledgerToDate);

      console.log('Parsed entryDate:', entryDate);
      console.log('Parsed currentEndDate:', currentEndDate);
      console.log('entryDate > currentEndDate:', entryDate > currentEndDate);

      // If the new entry's date is after the current end date, extend the range
      if (entryDate > currentEndDate) {
        // Format as 'YYYY-MM-DD'
        const yyyy = entryDate.getFullYear();
        const mm = String(entryDate.getMonth() + 1).padStart(2, '0');
        const dd = String(entryDate.getDate()).padStart(2, '0');
        const newToDate = `${yyyy}-${mm}-${dd}`;

        console.log(
          'Date range extending from:',
          this.ledgerToDate,
          'to:',
          newToDate,
        );
        this.ledgerToDate = newToDate;

        // Re-fetch transactions to update the view with the new date range
        await this.fetchTransactions();
        this.displayFooterMessage(
          `Date range extended to include new entry: ${newToDate}`,
        );

        console.log(
          'Date range extension completed. New ledgerToDate:',
          this.ledgerToDate,
        );
      }
    },

    async updateSelectedTransaction(transaction, savedId) {
      const id = Number(savedId) || Number(transaction?.Id);
      if (!transaction || !id) return;

      // Set this transaction as the selected row
      this.selectedRow = transaction;
      this.selectedRowIds = [id];

      // Sync selection for linking (Shift+L) but do not replace the open
      // document — user often has the target doc open when adding a ledger row.
      this.$emit('selectedLedgerRow', this.selectedRow);
      this.$emit('selectedLedgerRows', [this.selectedRow]);
      this.emitLedgerRowSelected(this.selectedRow, {
        openLinkedDocument: false,
      });

      await this.scrollLedgerRowIntoView(id, transaction);
      this.displayFooterMessage('Transaction saved and selected');
    },

    async scrollLedgerRowIntoView(savedId, transaction = null) {
      const id = Number(savedId);
      const findEl = () => document.querySelector(`#row-${id}`);

      for (let attempt = 0; attempt < 15; attempt++) {
        await this.$nextTick();
        let el = findEl();
        if (!el && attempt === 2) {
          // Row still not in DOM — relax common filters that hide new entries
          if (
            this.noDoubleEntry &&
            transaction &&
            transaction.Amount < 0 &&
            !String(transaction.Split || '').includes('-SPLIT-')
          ) {
            this.noDoubleEntry = false;
          }
          if (this.searchQuery || this.rawSearchQuery) {
            this.searchQuery = '';
            this.rawSearchQuery = '';
          }
          await this.$nextTick();
          el = findEl();
        }
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          el.classList.add('highlight-saved-row');
          setTimeout(() => {
            el.classList.remove('highlight-saved-row');
          }, 2000);
          return;
        }
        await new Promise((r) => setTimeout(r, 50));
      }
    },
  },
  watch: {
    rawSearchQuery: {
      handler: function (newVal) {
        this.debouncedUpdateSearchQuery(newVal);
      },
    },

    searchValue(newValue) {
      // Clear any previous highlight
      this.highlightRowId = null;

      // Automatically show splits for unmatched amount searches
      if (newValue && newValue.startsWith(':!m') && !this.showSplitsInTable) {
        this.showSplits();
      }

      // Parse highlight parameter if present
      if (newValue && newValue.includes('highlight=')) {
        const parts = newValue.split(',');
        const searchPart = parts.find((part) => part.startsWith(':id='));
        const highlightPart = parts.find((part) =>
          part.startsWith('highlight='),
        );

        if (highlightPart) {
          this.highlightRowId = parseInt(highlightPart.substring(10)); // Remove 'highlight=' prefix
          // Set both raw search query and search query without the highlight parameter
          const cleanSearchQuery = searchPart || newValue.split(',')[0];
          this.rawSearchQuery = cleanSearchQuery;
          this.searchQuery = cleanSearchQuery;
        } else {
          this.rawSearchQuery = newValue;
          this.searchQuery = newValue;
        }
      } else {
        // Set both raw search query and search query normally
        this.rawSearchQuery = newValue;
        if (newValue) {
          this.searchQuery = newValue;
        }
      }
    },

    searchQuery(newVal) {
      if (newVal) {
        const queries = newVal.split(',').map((query) => query.trim());
        let invertSearch = false;
        queries.forEach((query) => {
          invertSearch = false;
          if (query.startsWith('!')) {
            invertSearch = true;
            query = query.substring(1).trim();
          }
          if (query.startsWith(':u')) {
            // Filter for uncleared checks
            this.displayFooterMessage('Filtering for uncleared checks');
          } else if (query.startsWith(':v')) {
            // Filter for voided checks
            this.displayFooterMessage('Filtering for voided checks');
          } else if (query.startsWith(':m')) {
            // filter for matched
            this.displayFooterMessage('Filtering for matched transactions');
          } else if (query.startsWith(':!m')) {
            // Filter for unmatched
            this.displayFooterMessage('Filtering for unmatched transactions');
          } else if (query.startsWith(':a')) {
            // Filter for specific account
            this.displayFooterMessage('Filtering for selected account only');
          } else if (query.startsWith(':t')) {
            // Filter for today
            this.displayFooterMessage('Filtering for today only');
          } else if (query.startsWith(':f')) {
            // Filter for future transactions
            this.displayFooterMessage(
              'Filtering for futured dated transactions only',
            );
          } else {
            if (invertSearch) {
              this.displayFooterMessage('Inverting filter');
            }
          }
        });
      }
    },

    filteredTransactions(newVal) {
      console.log(
        'QB: filteredTransactions watcher called with',
        newVal.length,
        'transactions',
      );
      console.log('QB: Current highlightRowId:', this.highlightRowId);

      // If we have a specific row to highlight, select it
      if (this.highlightRowId && newVal.length > 0) {
        console.log(
          'QB: Looking for transaction with ID:',
          this.highlightRowId,
        );
        const targetRow = newVal.find(
          (transaction) => transaction.Id === this.highlightRowId,
        );
        if (targetRow) {
          console.log('QB: Found target row:', targetRow.Id, targetRow.Name);
          this.selectedRow = targetRow;
          this.selectedRowIds = [targetRow.Id];
          this.$emit('selectedLedgerRow', this.selectedRow);
          this.$emit('selectedLedgerRows', [targetRow]);
          // Programmatic highlight from search — keep open document
          this.emitLedgerRowSelected(targetRow, {
            openLinkedDocument: false,
          });

          // Scroll to the highlighted row
          this.$nextTick(() => {
            const element = document.querySelector(
              `[data-row-id="${targetRow.Id}"]`,
            );
            if (element) {
              console.log('QB: Found DOM element for row, scrolling to it');
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('selected-row-scroll');
            } else {
              console.log(
                'QB: Could not find DOM element for row ID:',
                targetRow.Id,
              );
            }
          });
          return;
        } else {
          console.log('QB: Target row not found in filtered transactions');
          console.log(
            'QB: Available transaction IDs:',
            newVal.map((t) => t.Id),
          );
        }
      }

      // Only auto-select if there's exactly one transaction AND no current selection
      if (newVal.length === 1 && this.selectedRowIds.length === 0) {
        const row = newVal[0];
        this.selectedRow = row;
        this.selectedRowIds = [row.Id];
        this.$emit('selectedLedgerRow', row);
        this.$emit('selectedLedgerRows', [row]);
        this.emitLedgerRowSelected(row, { openLinkedDocument: false });
      }
      // Don't clear existing selections when there are multiple transactions
    },

    selectedRow(newVal) {
      if (newVal) {
        console.log('selectedRow changed to:', newVal.Id, 'Name:', newVal.Name);
      } else {
        console.log('selectedRow cleared');
      }
    },

    // Watch for changes to highlightRowId when the filtered transactions stay the same
    highlightRowId(newVal) {
      console.log('QB: highlightRowId watcher called with:', newVal);
      if (newVal && this.filteredTransactions.length > 0) {
        const targetRow = this.filteredTransactions.find(
          (transaction) => transaction.Id === newVal,
        );
        if (targetRow) {
          console.log(
            'QB: highlightRowId watcher found target row:',
            targetRow.Id,
            targetRow.Name,
          );
          this.selectedRow = targetRow;
          this.selectedRowIds = [targetRow.Id];
          this.$emit('selectedLedgerRow', this.selectedRow);
          this.$emit('selectedLedgerRows', [targetRow]);
          this.emitLedgerRowSelected(targetRow, {
            openLinkedDocument: false,
          });

          // Scroll to the highlighted row
          this.$nextTick(() => {
            const element = document.querySelector(
              `[data-row-id="${targetRow.Id}"]`,
            );
            if (element) {
              console.log(
                'QB: highlightRowId watcher found DOM element, scrolling to it',
              );
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('selected-row-scroll');
            } else {
              console.log(
                'QB: highlightRowId watcher could not find DOM element for row ID:',
                targetRow.Id,
              );
            }
          });
        } else {
          console.log(
            'QB: highlightRowId watcher could not find target row in filtered transactions',
          );
        }
      }
    },

    // Watch for changes to date range props from MainPage
    ledgerFromDateProp: {
      handler(newVal) {
        if (newVal && newVal !== this.ledgerFromDate) {
          console.log(
            'QB: Updating internal ledgerFromDate from prop:',
            newVal,
          );
          this.ledgerFromDate = newVal;
        }
      },
      immediate: true,
    },

    ledgerToDateProp: {
      handler(newVal) {
        if (newVal && newVal !== this.ledgerToDate) {
          console.log('QB: Updating internal ledgerToDate from prop:', newVal);
          this.ledgerToDate = newVal;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.connectSocket();

    this.initializeData();

    // Handle URL parameters for shared searches
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');

    // Only process URL parameters if this is a ledger page share
    if (pageParam === 'ledger') {
      const searchParam = urlParams.get('q');
      const startParam = urlParams.get('start');
      const endParam = urlParams.get('end');

      if (searchParam) {
        this.searchQuery = searchParam;
        this.rawSearchQuery = searchParam;
      }
      if (startParam) {
        this.ledgerFromDate = startParam;
      }
      if (endParam) {
        this.ledgerToDate = endParam;
      }
      // Account is now handled in fetchAccounts() to avoid timing issues
    }

    document.addEventListener('keydown', this.handleKeydown);
    eventBus.on('closeLedgerEntry', this.closeEditDialog);
    eventBus.on('closeAddressModal', this.closeAddressModal);
    eventBus.on('ledgerEntryPrintCheck', this.handlePrintCheck);
    eventBus.on('insertNewLedgerRow', this.handleNewLedgerRow);
    eventBus.on(
      'createSplitChildrenFromBankRows',
      this.handleCreateSplitChildrenFromBankRows,
    );
    eventBus.on('bankRowSelected', this.handleBankRowSelected);
    eventBus.on('set_search_input', this.setSearchInput);
    eventBus.on('ledgerEntrySaved', this.handleLedgerEntrySaved);

    // Listen for successful matches from MainPage.vue
    eventBus.on('matchSuccessful', this.handleMatchSuccess);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    eventBus.off('closeLedgerEntry', this.closeEditDialog);
    eventBus.off('closeAddressModal', this.closeAddressModal);
    eventBus.off('ledgerEntryPrintCheck', this.handlePrintCheck);
    eventBus.off('insertNewLedgerRow', this.handleNewLedgerRow);
    eventBus.off(
      'createSplitChildrenFromBankRows',
      this.handleCreateSplitChildrenFromBankRows,
    );
    eventBus.off('bankRowSelected', this.handleBankRowSelected);
    eventBus.off('set_search_input', this.setSearchInput);
    eventBus.off('ledgerEntrySaved', this.handleLedgerEntrySaved);
    eventBus.off('matchSuccessful', this.handleMatchSuccess);
  },
};
</script>

<style scoped>
.title {
  font-size: 0.9em;
  margin-bottom: 7px;
  font-weight: bold;
  font-family: calibri;
  flex-grow: 1;
  text-align: left;
}

.table-container {
  width: 1280px;
  background: white;
  overflow-x: auto;
  height: 300px; /* Adjust the height as needed */
  user-select: none;
  overflow-y: auto;
  scrollbar-width: none; /* For Firefox */
}

.table-container::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit-based browsers */
}

.table-container-splits {
  width: 1300px;
  overflow-x: auto;
  height: 263px; /* Adjust the height as needed */
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;
}

.close-split-details {
  width: 1280px;
  background-color: #c4c4c4;
  padding: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  text-align: right;
}

.table {
  width: 1280px;
  border-collapse: collapse;
  font-size: 11px; /* Smaller font size */
}

.table th {
  padding: 3px;
  text-align: left;
}

.table td {
  padding: 3px 3px 3px 3px;
  border: 1pt solid rgb(219, 216, 216);
  text-align: left;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #a898b7;
  padding: 3px;
  color: white; /* White text color */
  z-index: unset;
}

.table th.right-align,
.table td.right-align {
  text-align: right;
  min-width: 65px;
}
.table th.center-align,
.table td.center-align {
  text-align: center;
  max-width: 50px;
  min-width: 50px;
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipses for overflow */
}
.table th.icon-column,
.table td.icon-column {
  text-align: center;
  max-width: 20px;
}

.table th.wide-column,
.table td.wide-column {
  max-width: 165px;
  min-width: 140px;
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipses; /* Add ellipses for overflow */
}

.table th.fixed-column,
.table td.fixed-column {
  min-width: 85px;
  white-space: nowrap; /* Prevent text wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipses; /* Add ellipses for overflow */
}

.text-ellipsis {
  white-space: nowrap; /* Prevent text wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipses; /* Add ellipses for overflow */
  width: 180px;
}

.table tbody tr:nth-child(even) {
  background-color: #dddbdb; /* Light gray for even rows */
}

.table tbody tr:nth-child(odd) {
  background-color: white; /* White for odd rows */
}

.search-container {
  background-color: #dddddd;
  display: flex;
  gap: 5px;
  margin-bottom: 0px;
  max-width: 1270px;
  min-width: 1000x;
  justify-content: space-between;
  align-items: center;
  padding: 5px; /* Add some padding to ensure it doesn't touch the edges */
  padding-bottom: 8px; /* Add some padding to ensure it doesn't touch the edges */
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.search-input {
  width: 80px;
  padding: 5px;
  height: 13px;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.clear-button {
  cursor: pointer;
  margin-left: 5px;
  color: white;
  background-color: #9487a1;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 2px;
  width: 24px;
}

.row-count {
  /* margin-left: 10px; */
  font-size: 9pt;
  margin-top: 7px;
  color: #535353;
  font-style: italic;
  margin-left: 10px;
}
:deep(.selected-row) {
  background-color: rgb(226 203 235) !important;
}

.selected-row-totals {
  font-size: 9pt;
  font-style: italic;
  color: #535353;
  margin-left: 15px;
  margin-right: 10px;
  width: 270px;
  padding-top: 7px;
}

.totals-wrapper {
  margin-right: 0;
}

:deep(.highlight) {
  background-color: #e7a4f1;
}

:deep(.highlight-saved-row) {
  background-color: #a3f4a3 !important;
  transition: background-color 1s ease;
}

.from-to-label {
  margin-left: 5px;
  font-size: 9pt;
  position: relative;
}

.month-shortcuts {
  position: absolute;
  top: -13px;
  left: -57px;
  display: flex;
}

.month-button {
  width: 12px;
  height: 12px;
  font-size: 8px;
  background-color: #f5f5f5;
  border: 1px solid #bababa;
  border-radius: 2px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  transition: background-color 0.2s;
}

.month-button:hover {
  background-color: #e0e0e0;
  color: #333;
}

.year-button {
  background-color: #f5f5f5;
  border: 1px solid #bababa;
  border-radius: 2px;
  font-size: 8px;
  color: #333;
  cursor: pointer;
  width: 16px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s;
}

.year-button:hover {
  background-color: #e0e0e0;
  color: #333;
}

.year-selected {
  background-color: #888;
  color: #fff;
}

.year-selected:hover {
  background-color: #666;
  color: #fff;
}

.year-month-gap {
  width: 12px;
}

.date-picker {
  width: 60px;
  height: 24px;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.autoselect-label {
  font-size: 0.8em;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 5px;
  font-style: italic;
  color: #888888;
}

.clear-button {
  cursor: pointer;
  margin-left: 5px;
  color: white;
  background-color: #9487a1;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 25px;
}
.select-credits-button {
  color: white;
  background-color: #9487a1;
  margin-left: 0px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 25px;
}

.select-credits-button-faded {
  color: white;
  background-color: #696073;
  border: 1pt solid #634f69;
  margin-left: 0px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 25px;
}

.show-splits-button {
  color: white;
  background-color: #4b0082;
}

.title-splits-details {
  font-size: 0.9em;
  margin-bottom: 3px;
  background-color: #dddddd;
  display: flex;
  gap: 5px;
  margin-bottom: 0px;
  max-width: 1275px;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.splits-total {
  font-weight: bold;
  font-size: 0.8em;
}

.splits-member {
  color: #9f84a5;
}

.transactions-footer {
  height: 20px;
  max-width: 1270px;
  background-color: #c4c4c4;
  padding: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  text-align: left;
  display: flex;
  justify-content: left;
}

.footer-message {
  width: 99%;
  padding: 3px;
  font-size: 9pt;
  color: white;
  background: #a080a8;
  transition: opacity 1s;
  padding-left: 8px;
  padding-right: 8px;
}

.fade-out {
  opacity: 0;
}

.checkmark {
  color: green;
  font-size: 0.9em; /* Adjust the size as needed */
}

.nothing-found {
  margin-top: 15px;
  margin-left: 15px;
  font-size: 14pt;
  color: #c3c2c2;
}

.table-scrollable-body {
  /* Hide scrollbar for WebKit-based browsers */
  overflow-y: scroll;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  -ms-overflow-style: none; /* Hide scrollbar for Internet Explorer and Edge */
}

.table-scrollable-body::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit-based browsers */
}

/* Optional: Ensure the scrollable area does not overlap */
.table-scrollable-body {
  height: auto; /* Adjust the height as needed */
  max-height: 600px; /* Ensure it fits your layout */
  overflow-x: auto;
}

.account-selection {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  margin-left: auto;
  margin-top: -5px;
}

.title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: -30px;
  margin-bottom: 5px;
}

.account-selection label {
  margin-right: 10px; /* Adjust spacing between label and dropdown */
  font-size: 0.8em;
}

.account-selection select {
  margin-right: 10px; /* Adjust spacing between dropdown and button */
  font-size: 9pt;
  padding: 1px;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.boundary-row {
  border-top: 4px solid blue; /* Adjust thickness and color as needed */
}

.selected-row-scroll {
  scroll-margin-top: 30px; /* Adjust this value based on the height of your header */
}

.shadow-box {
  box-shadow: 9px 8px 7px rgba(0, 0, 0, 0.1);
  margin-left: 30px;
  width: 1282px;
}

.voided {
  border: 0;
}

.bank-ledger-title {
  margin-bottom: 7px;
  /* font-weight: bold; */
  font-family: calibri;
  font-size: 1.1em;
  margin-bottom: 0px;
  width: 1262px;
  margin-left: -5px;
  margin-top: -5px;
  background: #6e5875;
  color: #fdfdfd;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin-bottom: 5px;
}

.action-link {
  text-decoration: underline;
  color: #a080a8;
}

/* check viewer */
.check-viewer {
  position: absolute;
  top: 20%;
  left: calc(680px - 390px);
  width: 790px;
  height: 460px;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column; /* Ensures the header is above the iframe */
  background-color: #fff;
  border-radius: 5px;
}
/* ledger viewer */
.ledger-viewer {
  position: absolute;
  top: 10%;
  left: calc(680px - 390px);
  width: 790px;
  width: 790px;
  height: 764px;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 5px 6px 8px 5px rgba(0, 0, 0, 0.4);
}

/* Title bar styles */
.check-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #333;
  color: #eee;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  padding: 10px;
}

.check-viewer-title {
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
}

.check-viewer-close-button {
  border-radius: 4px;
  border: 1px solid #888;
  background: transparent;
  color: #eee;
  font-size: 16px;
  cursor: pointer;
  position: relative;
  margin-left: 5px;
  top: -1px;
}

.check-viewer-close-button:hover {
  color: #ff6666; /* Optional: change color on hover */
}

/* iframe styles */
iframe {
  width: 100%;
  min-height: 725px;
  height: 725px;
  border: none;
}

.protected-page {
  width: 1311px;
}

.transaction-deleted {
  text-decoration: line-through;
  color: #888;
}

/* Sortable header styles */
.sortable-header {
  user-select: none;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #a898b7;
}

.sort-indicator {
  margin-left: 5px;
  font-weight: bold;
  color: #007bff;
}

/* Num column width */
.table th.num-column,
.table td.num-column {
  min-width: 60px;
  width: 60px;
}

/* Share dialog popup styles */
.confirm-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200px;
  transform: translate(-50%, -50%);
  background: #ffffff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 7px 7px 8px 3px rgba(0, 0, 0, 0.2);
  z-index: 10020;
  border: 1px solid #888;
  color: #eee;
}

.confirm-message {
  font-size: 1em;
  margin-bottom: 20px;
  color: #333;
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.confirm-buttons button {
  background: #a080a8;
  padding: 6px;
  width: 70px;
  color: #fff;
  border-radius: 4px;
  border: 1pt solid #ddd;
}
</style>
